/**
 * Trainer/admin LiveKit room shell.
 * Connect steps live in LiveKitSessionProvider (token → room → media).
 * Top bar + topic progress ported from Figma NewFlowStudentLiveView.
 * Trainer footer tools: Pause for Q&A + Manage topic (right corner).
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  useMaybeRoomContext,
  useParticipants,
} from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import {
  LiveKitSessionProvider,
  useLiveKitSession,
} from "./LiveKitSessionContext";
import { isParticipantWaiting, parseParticipantRole } from "./config";
import type { LiveSessionNotice } from "./LiveSessionNoticeToast";
import { LiveKitMainStage } from "./LiveKitMainStage";
import { LiveKitVideoTiles } from "./LiveKitVideoTiles";
import { useSelectiveTrackSubscription } from "./useSelectiveTrackSubscription";
import { LiveKitControlBar } from "./LiveKitControlBar";
import { LiveKitTopBar } from "./LiveKitTopBar";
import { StartQaPauseModal } from "./StartQaPauseModal";
import { ManageTopicModal, formatTimeLimitLabel, type TopicManageAction } from "./ManageTopicModal";
import {
  decodeLiveTopicManage,
  encodeLiveTopicManage,
} from "./liveTopicManageSync";
import { ConfirmPushQuizModal } from "./ConfirmPushQuizModal";
import { upsertTopicTiming } from "./topicTimingApi";
import { setSessionQaPaused } from "./qaPauseApi";
import { fetchAttendanceEngagement } from "./attendanceEngagementApi";
import {
  clampQaDurationMinutes,
  DEFAULT_QA_DURATION_MINUTES,
  isQaPauseOver,
  loadQaWindow,
  qaPauseEndsAtMs,
  setQaDurationForSession,
} from "../codelive/create/utils/sessionQaStorage";
import { loadLiveTiming } from "./topicProgress/timingPersistence";
import { TopicAbsorptionPanel } from "./TopicAbsorptionPanel";
import { TrainerQaPanel } from "./TrainerQaPanel";
import LiveSessionNoticeToast from "./LiveSessionNoticeToast";
import { TrainerAnnouncementsPanel } from "./TrainerAnnouncementsPanel";
import { TrainerPollsPanel } from "./TrainerPollsPanel";
import { TrainerChatPanel } from "./TrainerChatPanel";
import { AttendanceEngagementPanel } from "./AttendanceEngagementPanel";
import type { AttendanceEngagementFilter } from "./AttendanceEngagementPanel";
import { DistractionReportPanel } from "./DistractionReportPanel";
import { SessionTopicsPage } from "./SessionTopicsPage";
import {
  LiveSessionPipBanner,
  LiveSessionPipPortal,
} from "./LiveSessionPipPortal";
import { useLiveSessionDocumentPip } from "./useLiveSessionDocumentPip";
import { useLiveSessionFlowClass } from "./useLiveSessionFlowClass";
import { STAGE_FULLSCREEN_EVENT } from "./LocalScreenSharePreview";
import { SessionDetailsPopover } from "./SessionDetailsPopover";
import { useLiveSessionClockSync } from "./useLiveSessionClockSync";
import {
  mergeTopicTimingIfChanged,
  useSessionTopicTimingPoll,
} from "./useSessionTopicTimingPoll";
import { useLiveSessionPolicySync } from "./useLiveSessionPolicySync";
import { formatIstClock } from "../utils/formatIstClock";
import { useLiveQuizSync } from "./useLiveQuizSync";
import { useLiveCollaborationSync } from "./useLiveCollaborationSync";
import { useLiveReactionSync } from "./useLiveReactionSync";
import { ReactionPicker } from "./ReactionPicker";
import { FloatingReactionsOverlay } from "./FloatingReactionsOverlay";
import { pushLiveQuiz, fetchLiveQuizScores, fetchGraspVotes, finalizeGraspFeedback, liveQuizPushErrorMessage } from "./liveQuizApi";
import type { QuizSlot, ActiveLiveQuiz } from "./liveQuizTypes";
import { isLiveQuiz } from "./liveQuizTypes";
import { TrainerLiveQuizReport } from "./TrainerLiveQuizReport";
import { ParticipantModerationPanel } from "./ParticipantModerationPanel";
import { useLiveHandRaises } from "./useLiveHandRaises";
import type { HandRaiseEntry } from "./liveHandTypes";
import { MediaJoinSettingsPanel } from "./MediaJoinSettingsPanel";
import { useHostModerationSync } from "./useHostModerationSync";
import {
  joinDefaultsForRole,
  normalizeSessionSettings,
  type LiveSessionSettings,
} from "./sessionSettings";
import {
  MeetingToolPanel,
  MeetingToolsRail,
  type MeetingToolId,
} from "./meetingTools";
import { useMeetingToolsResize } from "./meetingTools/useMeetingToolsResize";
import { useIsMobile } from "./useIsMobile";
import { LayoutGrid } from "lucide-react";
import { SessionRecordingPanel } from "./SessionRecordingPanel";
import { OfflineDisconnectModal } from "./OfflineDisconnectModal";
import { ScreenSharePrivacyControls } from "./ScreenSharePrivacyControls";
import type { RecordingStatus } from "./recordingApi";
import {
  getRoutePrefix,
  getViewSessionsPath,
  convertTo12HourFormat,
} from "../LMS/TR/files/sessionUtils/sessionUtils";
import { endLiveSession, pauseLiveSession } from "../liveSession/api";
import { encodeLiveSessionEnded } from "./liveSessionEndSync";
import {
  encodeLiveSessionPaused,
  formatLiveSessionPausedStudentMessage,
} from "./liveSessionPauseSync";
import { useLiveSessionEndSync } from "./useLiveSessionEndSync";
import { useLiveSessionPauseSync } from "./useLiveSessionPauseSync";
import { isSessionEndedLiveKitError } from "./liveKitConnectErrors";
import { createGuardedAsyncPoller } from "./guardedAsyncPoller";
import { livekitAuthService as authService } from "../utils/authService";
import {
  AUTH_SESSION_EXPIRED_EVENT,
  isAuthHttpError,
  isAuthSessionExpired,
} from "../utils/authSessionGuard";
import { fetchCoursesByTrack } from "../utils/courseUtils";
import { fetchBatchesByCourseSingle } from "../utils/batchUtils";
import {
  normalizeTopicPlan,
  filterTopicPlanForWorkshopDay,
  applyTopicTimingRecords,
  useLiveTopicProgress,
  resolveActualDurationSec,
  type SessionTopicPlan,
} from "./topicProgress";
import type { TopicTimingRecord } from "./topicProgress/types";
import {
  syncedClockFromTopicPlan,
  mergeSessionClockWithTopicTiming,
} from "./topicProgress/topicTimingClockFallback";

import { exitToSessionList } from "./sessionListExit";

/** Publish wall-clock duration for Time taken (never a stale field=0). */
function durationSecForClockPublish(
  item: Pick<
    SessionTopicPlan,
    "actualStartAt" | "actualEndAt" | "actualDurationSec" | "skipped"
  >,
): number | null {
  const sec = resolveActualDurationSec(item as SessionTopicPlan);
  if (sec == null) return null;
  // Skipped may legitimately be 0; completed must not publish a bogus 0.
  if (sec <= 0 && !item.skipped) return null;
  return Math.floor(sec);
}

function asIdList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .flatMap((id) => asIdList(id))
      .filter((id, index, arr) => id && arr.indexOf(id) === index);
  }
  const single = String(value || "").trim();
  if (!single) return [];
  if (single.includes(",") || single.includes("|")) {
    return single
      .replace(/\|/g, ",")
      .split(",")
      .map((part) => part.trim())
      .filter((id, index, arr) => id && arr.indexOf(id) === index);
  }
  return [single];
}

/** True for values like "user3", "U123", raw ids — not a person name. */
function looksLikeUserId(value?: string): boolean {
  const v = String(value || "").trim();
  if (!v) return true;
  if (/^user\d+$/i.test(v)) return true;
  if (/^[Uu]\d+$/.test(v)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(v)) return true;
  if (!/\s/.test(v) && /^[a-z0-9_-]{2,32}$/i.test(v) && !/[A-Z][a-z]/.test(v)) {
    // short lowercase token without space — likely an id, not "Alex Rivera"
    return /^(user|trainer|admin|id)/i.test(v) || /^\d+$/.test(v);
  }
  return false;
}

function resolveTrainerDisplayName(
  ...candidates: Array<string | undefined | null>
): string {
  for (const raw of candidates) {
    const name = String(raw || "").trim();
    if (name && !looksLikeUserId(name)) return name;
  }
  return "Trainer";
}

async function resolveCourseBatchLabels(params: {
  courseIds: string[];
  batchIds: string[];
  trackId?: string;
}): Promise<{ courseLabel: string; batchLabel: string }> {
  const { courseIds, batchIds, trackId } = params;
  let courseLabel = "";
  let batchLabel = "";

  try {
    const track =
      trackId ||
      sessionStorage.getItem("meet_track_id") ||
      "";
    let courses: { course_id: string; course_name: string }[] = [];
    if (track) {
      courses = await fetchCoursesByTrack(track);
    }
    const matchedCourses = courseIds
      .map(
        (id) =>
          courses.find((c) => String(c.course_id) === String(id))?.course_name ||
          "",
      )
      .filter(Boolean);
    courseLabel = matchedCourses.join(", ");

    const batchNames: string[] = [];
    const coursesForBatches =
      courseIds.length > 0
        ? courseIds
        : courses.map((c) => c.course_id).slice(0, 1);
    for (const courseId of coursesForBatches) {
      const batches = await fetchBatchesByCourseSingle(courseId);
      for (const bid of batchIds) {
        const name = batches.find(
          (b) => String(b.batch_id) === String(bid),
        )?.batch_name;
        if (name && !batchNames.includes(name)) batchNames.push(name);
      }
    }
    batchLabel = batchNames.join(", ");
  } catch {
    // keep empty; nav state / sessionStorage may still fill
  }

  return { courseLabel, batchLabel };
}
type LiveSessionRoomProps = {
  role?: "trainer" | "student";
};

export type LiveSessionNavState = {
  sessionName?: string;
  scheduledEndTime?: string;
  expectedEndTime?: string;
  startTime?: string;
  durationMinutes?: number;
  topicPlan?: unknown;
  topicCovered?: string;
  courseLabel?: string;
  batchLabel?: string;
  category?: string;
  trainerName?: string;
  trainerAvatarUrl?: string;
  /** Session-assigned trainer user id — only they may manage topics. */
  assignedTrainerId?: string;
  streamingMode?: string;
  courseId?: string | string[];
  batchId?: string | string[];
  sessionSettings?: LiveSessionSettings | Record<string, unknown>;
  /** Active workshop day (0-based) — filters topic progress to that day only. */
  workshopDayIndex?: number;
};

/** Matches student TopicGraspModal DEFAULT_AUTO_CLOSE_SECONDS. */
const GRASP_FEEDBACK_SECONDS = 120;

function formatClockLabel(timeRaw?: string): string | undefined {
  const t = String(timeRaw || "").trim();
  if (!t) return undefined;
  if (/AM|PM/i.test(t)) return t.replace(/^0/, "");
  try {
    return convertTo12HourFormat(t).replace(/^0/, "");
  } catch {
    return t;
  }
}

/** Must only mount inside LiveKitRoom (uses useParticipants). */
function TrainerWaitingAdmitWatcher({
  onWaitingStudent,
  onWaitingCountChange,
}: {
  onWaitingStudent: (notice: LiveSessionNotice) => void;
  onWaitingCountChange?: (count: number) => void;
}) {
  const participants = useParticipants();
  const seenWaitingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const waiting = participants.filter((p) => {
      if (p.isLocal) return false;
      if (parseParticipantRole(p.metadata, p.identity) === "trainer") {
        return false;
      }
      return isParticipantWaiting(p.metadata);
    });
    onWaitingCountChange?.(waiting.length);

    const waitingIds = new Set(waiting.map((p) => p.identity));
    for (const id of Array.from(seenWaitingRef.current)) {
      if (!waitingIds.has(id)) seenWaitingRef.current.delete(id);
    }
    for (const p of waiting) {
      if (seenWaitingRef.current.has(p.identity)) continue;
      seenWaitingRef.current.add(p.identity);
      const name = (p.name || p.identity || "A student").trim();
      const others = waiting.length - 1;
      const text =
        others > 0
          ? `${name} and ${others} other${others === 1 ? "" : "s"} are waiting to join — open Participants to Admit`
          : `${name} is waiting to join — open Participants to Admit`;
      onWaitingStudent({
        id: `wait-${p.identity}-${Date.now()}`,
        text,
        action: { type: "open_participants" },
        durationMs: 6000,
      });
    }
  }, [participants, onWaitingStudent, onWaitingCountChange]);

  return null;
}

function RoomBody({
  sessionId,
  role,
  sessionMeta,
  topics,
  startTime,
  scheduledStartTime,
  sessionStartedAtIso,
  durationMinutes,
  plannedQaMinutesFromSession,
}: {
  sessionId: string;
  role: "trainer" | "student";
  sessionMeta: LiveSessionNavState;
  topics: SessionTopicPlan[];
  startTime: string;
  scheduledStartTime?: string;
  /** ISO actual/scheduled start for the never-pausing session timer. */
  sessionStartedAtIso?: string | null;
  durationMinutes: number;
  /** Q&A minutes from session record (parent fetch); overrides storage when set. */
  plannedQaMinutesFromSession?: number | null;
}) {
  const navigate = useNavigate();
  const liveKit = useLiveKitSession();
  const displayName =
    sessionStorage.getItem("Name") ||
    sessionStorage.getItem("email") ||
    (role === "trainer" ? "Trainer" : "Student");
  const trainerId =
    sessionStorage.getItem("Id") ||
    sessionStorage.getItem("email") ||
    "trainer";
  const prefix = getRoutePrefix();
  const homePath = getViewSessionsPath(prefix);
  const sessionTitle =
    sessionMeta.sessionName?.trim() || `Session #${sessionId}`;
  const isTrainer = role === "trainer";
  const currentTrainerId = String(
    sessionStorage.getItem("Id") || sessionStorage.getItem("email") || "",
  ).trim();
  const currentTrainerEmail = String(sessionStorage.getItem("email") || "")
    .trim()
    .toLowerCase();
  const assignedTrainerId = String(sessionMeta.assignedTrainerId || "").trim();
  const isAssignedSessionTrainer =
    isTrainer &&
    assignedTrainerId.length > 0 &&
    (assignedTrainerId === currentTrainerId ||
      assignedTrainerId.toLowerCase() === currentTrainerEmail);
  const canManageSession =
    isTrainer &&
    (assignedTrainerId
      ? isAssignedSessionTrainer
      : liveKit.isPrimaryTrainer === true);
  const isPrimaryTrainer = canManageSession;
  const [endingSession, setEndingSession] = useState(false);
  const [endSessionError, setEndSessionError] = useState<string | null>(null);
  const room = useMaybeRoomContext();
  const liveParticipants = useParticipants();
  const [engagementStudentPeers, setEngagementStudentPeers] = useState<
    Array<{ identity: string; display_name: string }>
  >([]);
  const chatStudentPeers = useMemo(() => {
    const map = new Map<string, { identity: string; display_name: string }>();
    for (const p of liveParticipants) {
      const identity = String(p.identity || "").trim();
      if (!identity || identity === trainerId) continue;
      if (parseParticipantRole(p.metadata, p.identity) === "trainer") continue;
      if (isParticipantWaiting(p.metadata)) continue;
      map.set(identity, {
        identity,
        display_name: String(p.name || "").trim() || identity,
      });
    }
    for (const s of engagementStudentPeers) {
      const identity = String(s.identity || "").trim();
      if (!identity || identity === trainerId || map.has(identity)) continue;
      map.set(identity, {
        identity,
        display_name: String(s.display_name || "").trim() || identity,
      });
    }
    return Array.from(map.values()).sort((a, b) =>
      a.display_name.localeCompare(b.display_name),
    );
  }, [liveParticipants, trainerId, engagementStudentPeers]);
  const [waitingAdmitNotice, setWaitingAdmitNotice] =
    useState<LiveSessionNotice | null>(null);
  const [waitingAdmitCount, setWaitingAdmitCount] = useState(0);
  const [handRaiseNotice, setHandRaiseNotice] =
    useState<LiveSessionNotice | null>(null);
  const leave = () => {
    try {
      liveKit.stopRoom();
    } catch {
      try {
        room?.disconnect();
      } catch {
        /* ignore */
      }
    }
    exitToSessionList(homePath);
  };

  useLiveSessionEndSync({
    enabled: isTrainer && !isPrimaryTrainer && liveKit.inRoom,
    onSessionEnded: () => {
      try {
        sessionStorage.setItem(
          "codelive_removed_notice",
          "This session has been ended by your trainer.",
        );
      } catch {
        /* ignore */
      }
      try {
        liveKit.stopRoom();
      } catch {
        try {
          room?.disconnect();
        } catch {
          /* ignore */
        }
      }
      leave();
    },
  });

  useLiveSessionPauseSync({
    enabled: isTrainer && !isPrimaryTrainer && liveKit.inRoom,
    onSessionPaused: (msg) => {
      const resume = msg.expected_resume_at
        ? (() => {
            try {
              return new Date(msg.expected_resume_at).toLocaleString();
            } catch {
              return msg.expected_resume_at;
            }
          })()
        : null;
      const copy =
        msg.message ||
        (resume
          ? `The trainer has paused this session. It will resume at ${resume}.`
          : "The trainer has paused this session. Join opens after the trainer resumes.");
      try {
        sessionStorage.setItem("codelive_removed_notice", copy);
      } catch {
        /* ignore */
      }
      try {
        liveKit.stopRoom();
      } catch {
        try {
          room?.disconnect();
        } catch {
          /* ignore */
        }
      }
      leave();
    },
  });

  const endOrLeave = async () => {
    // ---------------------------------------------------------------
    // step 1: Co-trainers leave the room and return to the session list
    // ---------------------------------------------------------------
    if (!canManageSession) {
      leave();
      return;
    }

    // ---------------------------------------------------------------
    // step 2: Trainer End → notify students, mark LMS session completed
    // ---------------------------------------------------------------
    if (endingSession) return;
    setEndingSession(true);
    setEndSessionError(null);
    try {
      if (room?.localParticipant) {
        try {
          await room.localParticipant.publishData(
            encodeLiveSessionEnded({
              message: "This session has been ended by your trainer.",
            }) as Uint8Array<ArrayBuffer>,
            { reliable: true },
          );
          // Brief pause so the data message reaches students before room teardown.
          await new Promise((r) => window.setTimeout(r, 400));
        } catch (broadcastErr) {
          console.warn("Session-end broadcast failed", broadcastErr);
        }
      }
      const userId = sessionStorage.getItem("Id") || "";
      await endLiveSession(sessionId, userId);
      leave();
    } catch (err) {
      console.error("Failed to end live session", err);
      const message =
        err instanceof Error && err.message.trim()
          ? err.message.trim()
          : "Unable to end this session. Please try again from the sessions list.";
      setEndSessionError(message);
    } finally {
      setEndingSession(false);
    }
  };

  const isLcmSession =
    String(sessionMeta.category || "")
      .trim()
      .toLowerCase() === "mandatory";
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pauseResumeLocal, setPauseResumeLocal] = useState("");
  const [pausingSession, setPausingSession] = useState(false);
  const [pauseSessionError, setPauseSessionError] = useState<string | null>(
    null,
  );

  const confirmPauseSession = async () => {
    if (!canManageSession || !isLcmSession) return;
    if (pausingSession || endingSession) return;
    const raw = pauseResumeLocal.trim();
    if (!raw) {
      setPauseSessionError("Expected resume date and time is required.");
      return;
    }
    const resumeAt = new Date(raw);
    if (Number.isNaN(resumeAt.getTime()) || resumeAt.getTime() <= Date.now()) {
      setPauseSessionError("Expected resume must be a future date and time.");
      return;
    }
    setPausingSession(true);
    setPauseSessionError(null);
    try {
      const resumeIso = resumeAt.toISOString();
      const studentCopy = formatLiveSessionPausedStudentMessage(resumeIso);
      if (room?.localParticipant) {
        try {
          await room.localParticipant.publishData(
            encodeLiveSessionPaused({
              message: studentCopy.message,
              expected_resume_at: resumeIso,
            }) as Uint8Array<ArrayBuffer>,
            { reliable: true },
          );
          // Give students time to receive the pause notice before room teardown.
          await new Promise((r) => window.setTimeout(r, 800));
        } catch (broadcastErr) {
          console.warn("Session-pause broadcast failed", broadcastErr);
        }
      }
      await pauseLiveSession(sessionId, resumeIso);
      setPauseDialogOpen(false);
      leave();
    } catch (err) {
      console.error("Failed to pause live session", err);
      setPauseSessionError(
        err instanceof Error && err.message.trim()
          ? err.message.trim()
          : "Unable to pause this session. Please try again.",
      );
    } finally {
      setPausingSession(false);
    }
  };

  const [qaPaused, setQaPaused] = useState(false);
  const [qaPauseModalOpen, setQaPauseModalOpen] = useState(false);
  const [qaEndsAt, setQaEndsAt] = useState<number | null>(null);
  const [plannedQaMinutes, setPlannedQaMinutes] = useState(() =>
    clampQaDurationMinutes(
      loadQaWindow(sessionId).durationMinutes || DEFAULT_QA_DURATION_MINUTES,
    ),
  );
  const autoQaStartedRef = useRef(false);

  useEffect(() => {
    if (
      plannedQaMinutesFromSession == null ||
      !Number.isFinite(plannedQaMinutesFromSession) ||
      plannedQaMinutesFromSession <= 0
    ) {
      return;
    }
    setPlannedQaMinutes(clampQaDurationMinutes(plannedQaMinutesFromSession));
  }, [plannedQaMinutesFromSession]);

  const [absenteeCount, setAbsenteeCount] = useState<number | null>(null);
  const [manageTopicOpen, setManageTopicOpen] = useState(false);
  const [manageTopicEarlyAccess, setManageTopicEarlyAccess] =
    useState(true);
  const [sessionDetailsOpen, setSessionDetailsOpen] = useState(false);
  /** Quiz Scores docked via Meeting Tools (activeTool === "quiz-scores"). */
  /** All live-quiz pushes in this session (for Quiz Scores list). */
  const [sessionQuizzes, setSessionQuizzes] = useState<ActiveLiveQuiz[]>([]);
  /** Submission keys the trainer has already opened in Quiz Scores */
  const [seenQuizScoreKeys, setSeenQuizScoreKeys] = useState<Set<string>>(
    () => new Set(),
  );
  /** Grasp vote keys already viewed in Topic Absorption */
  const [seenGraspVoteKeys, setSeenGraspVoteKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [seenChatMessageIds, setSeenChatMessageIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [seenQaKeys, setSeenQaKeys] = useState<Set<string>>(() => new Set());
  const [seenAnnouncementIds, setSeenAnnouncementIds] = useState<Set<string>>(
    () => new Set(),
  );
  /** After bootstrap/hydrate baseline, only *new* items raise the tool badge. */
  const toolBadgesSeededRef = useRef(false);
  /** Incremental REST poll cursor — avoids 304 etag cache missing new rows. */
  const collabPollSinceRef = useRef<string>("");
  /** Q&A delta cursor (same 12s collab REST tick as announcements/chat). */
  const qaPollSinceRef = useRef<string>("");
  const [quizPushBusy, setQuizPushBusy] = useState(false);
  const [quizPushError, setQuizPushError] = useState<string | null>(null);
  const [toolsCollapsed, setToolsCollapsed] = useState(false);
  const [activeTool, setActiveTool] = useState<MeetingToolId | null>(null);
  /** Mobile Meeting Tools sheet (rail / panel overlay). */
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const isMobile = useIsMobile(768);
  const [focusQaId, setFocusQaId] = useState<string | null>(null);
  const [focusChatPeerId, setFocusChatPeerId] = useState<string | null>(null);
  /** Identities currently on the middle stage — list exclude + camera subscribe. */
  const [stageIdentities, setStageIdentities] = useState<string[]>([]);
  /** Identities currently in the right Participants rail. */
  const [railIdentities, setRailIdentities] = useState<string[]>([]);
  const onStageIdentitiesChange = useCallback((ids: string[]) => {
    setStageIdentities((prev) => {
      if (
        prev.length === ids.length &&
        prev.every((id, i) => id === ids[i])
      ) {
        return prev;
      }
      return ids;
    });
  }, []);
  const onRailIdentitiesChange = useCallback((ids: string[]) => {
    setRailIdentities((prev) => {
      if (
        prev.length === ids.length &&
        prev.every((id, i) => id === ids[i])
      ) {
        return prev;
      }
      return ids;
    });
  }, []);
  useEffect(() => {
    if (!liveKit.inRoom) {
      setStageIdentities([]);
      setRailIdentities([]);
      setWaitingAdmitCount(0);
      setWaitingAdmitNotice(null);
    }
  }, [liveKit.inRoom]);
  useEffect(() => {
    if (!isTrainer) {
      setRailIdentities([]);
      setWaitingAdmitCount(0);
    }
  }, [isTrainer]);

  // Stage: always pull cams. Rail: pull only when that student has camera on.
  useSelectiveTrackSubscription({
    enabled: liveKit.inRoom,
    stageIdentities,
    railIdentities: isTrainer ? railIdentities : [],
    maxCameraSubscriptions: 24,
  });

  const onWaitingStudent = React.useCallback((notice: LiveSessionNotice) => {
    setWaitingAdmitNotice(notice);
    setActiveTool("participants");
    setToolsCollapsed(false);
    if (isMobile) setMobileToolsOpen(true);
  }, [isMobile]);
  const onWaitingCountChange = React.useCallback((count: number) => {
    setWaitingAdmitCount(count);
  }, []);
  const onHandRaised = React.useCallback(
    (entry: HandRaiseEntry) => {
      const name = (entry.student_name || entry.student_id || "A student").trim();
      setHandRaiseNotice({
        id: `hand-${entry.student_id}-${entry.raised_at}`,
        text: `${name} raised their hand — open Participants to Approve mic`,
        action: { type: "open_participants" },
        durationMs: 8000,
      });
      // Keep main stage visible; Participants tool badge + toast carry the signal.
    },
    [],
  );
  const handRaises = useLiveHandRaises({
    enabled: Boolean(isTrainer && room),
    localIdentity: trainerId,
    localName: displayName,
    onHandRaised: isTrainer ? onHandRaised : undefined,
  });
  const [engagementFilter, setEngagementFilter] =
    useState<AttendanceEngagementFilter>("all");
  const {
    width: toolsWidth,
    setWidth: setToolsWidth,
    onResizePointerDown: onToolsResizePointerDown,
  } = useMeetingToolsResize();
  const quizScoresOpen = activeTool === "quiz-scores";
  const absorptionOpen = activeTool === "absorption";
  const [recordingStatus, setRecordingStatus] =
    useState<RecordingStatus>("idle");
  const [pauseAccumulatedSec, setPauseAccumulatedSec] = useState(() => {
    const persisted = loadLiveTiming(sessionId);
    return persisted?.pauseAccumulatedSec ?? 0;
  });
  const [pauseNowSec, setPauseNowSec] = useState(0);
  const pauseStartedAtRef = useRef<number | null>(null);
  const [preferMainWhileSharing, setPreferMainWhileSharing] = useState(false);
  const documentPip = useLiveSessionDocumentPip();
  const [quizPrompt, setQuizPrompt] = useState<{
    slot: QuizSlot;
    afterComplete?: boolean;
  } | null>(null);
  /** Pre-quiz dismissed with X — allow reopen without restarting the topic. */
  const [deferredQuizPrompt, setDeferredQuizPrompt] = useState<{
    slot: QuizSlot;
    afterComplete?: boolean;
  } | null>(null);

  useEffect(() => {
    if (canManageSession) return;
    setQuizPrompt(null);
    setDeferredQuizPrompt(null);
    setManageTopicOpen(false);
    setQaPauseModalOpen(false);
  }, [canManageSession]);

  const pendingCompleteAfterPostRef = useRef(false);
  /** After post-quiz/grasp: advance topic or only the current subtopic. */
  const pendingAdvanceModeRef = useRef<"topic" | "subtopic">("topic");
  /** After topic complete, auto-start this next topic id (no Start topic click). */
  const pendingAutoStartTopicIdRef = useRef<string | null>(null);
  /** Waiting for pushed post-quiz timer to end before advancing. */
  const [awaitingPostQuizEnd, setAwaitingPostQuizEnd] = useState(false);
  /**
   * After post-quiz ends: wait for grasp feedback (all votes or 2 min)
   * before completing the topic / auto-starting next / pre-quiz.
   */
  const [awaitingGraspFeedback, setAwaitingGraspFeedback] = useState(false);
  const [graspEndsAt, setGraspEndsAt] = useState<number | null>(null);
  const [graspTopicId, setGraspTopicId] = useState<string | null>(null);
  const [graspRemainingSec, setGraspRemainingSec] = useState(0);
  const [frozenProjectedEnd, setFrozenProjectedEnd] = useState<string | null>(
    null,
  );
  const graspAdvanceDoneRef = useRef(false);
  const graspWaitActiveRef = useRef(false);
  const postQuizAdvancePushIdRef = useRef<string | null>(null);
  /**
   * Topics already finishing (Completed/Skip applied, or post-quiz in flight).
   * Prevents Apply: Completed → second post-quiz for the same topic.
   */
  const topicCompletionLockRef = useRef<Set<string>>(new Set());
  /** Dedup topic-manage decisions (incl. +10m) so only one trainer's Apply wins. */
  const topicManageDecisionRef = useRef<Set<string>>(new Set());
  /** True while applying a peer trainer's topic-manage packet (don't re-broadcast). */
  const applyingRemoteTopicManageRef = useRef(false);
  /** topic_id → post quiz already pushed for completion flow */
  const postQuizPushedTopicIdsRef = useRef<Set<string>>(new Set());
  const [liveSessionSettings, setLiveSessionSettings] =
    useState<LiveSessionSettings>(() =>
      normalizeSessionSettings(sessionMeta.sessionSettings),
    );

  useEffect(() => {
    setLiveSessionSettings(
      normalizeSessionSettings(sessionMeta.sessionSettings),
    );
  }, [sessionMeta.sessionSettings]);

  // Fullscreen of local screen-share must not nest Document PiP into the capture.
  useEffect(() => {
    const onBeforeFullscreen = () => {
      documentPip.closePip();
      if (liveKit.isScreenShareEnabled) {
        setPreferMainWhileSharing(true);
      }
    };
    window.addEventListener(STAGE_FULLSCREEN_EVENT, onBeforeFullscreen);
    return () =>
      window.removeEventListener(STAGE_FULLSCREEN_EVENT, onBeforeFullscreen);
  }, [documentPip, liveKit.isScreenShareEnabled]);

  const hideTrainerChrome =
    isTrainer &&
    liveKit.isScreenShareEnabled &&
    !preferMainWhileSharing;
  const recordingActive =
    recordingStatus === "recording" || liveKit.isRecording;
  const recordingPaused = recordingStatus === "paused";
  /** True after room reported egress on — used to clear TopBar only when egress ends. */
  const sawEgressRecordingRef = useRef(false);

  const handleRecordingStarted = useCallback(() => {
    // ---------------------------------------------------------------
    // step 1: After Start — collapse tools + close Recording panel
    // Re-open from the rail to pause/stop; egress stays student view.
    // ---------------------------------------------------------------
    setToolsCollapsed(true);
    setActiveTool(null);
    setMobileToolsOpen(false);
  }, []);

  // Clear logical "recording" only after egress was observed active, then ended.
  // Avoids clearing right after Start (room.isRecording lags) and never clears "paused".
  useEffect(() => {
    if (liveKit.isRecording) {
      sawEgressRecordingRef.current = true;
      return;
    }
    if (recordingStatus === "paused" || recordingStatus === "idle") {
      sawEgressRecordingRef.current = false;
      return;
    }
    if (sawEgressRecordingRef.current && recordingStatus === "recording") {
      sawEgressRecordingRef.current = false;
      setRecordingStatus("stopped");
    }
  }, [liveKit.isRecording, recordingStatus]);

  useEffect(() => {
    if (!liveKit.isScreenShareEnabled) {
      setPreferMainWhileSharing(false);
    }
  }, [liveKit.isScreenShareEnabled]);

  useEffect(() => {
    if (!isMobile) setMobileToolsOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (hideTrainerChrome) {
      setActiveTool(null);
      setToolsCollapsed(true);
      setMobileToolsOpen(false);
      setSessionDetailsOpen(false);
      setManageTopicOpen(false);
      setQaPauseModalOpen(false);
    }
  }, [hideTrainerChrome]);

  // ---------------------------------------------------------------
  // step 1: While REC/PAUSED, keep Meeting Tools collapsed (UX only)
  // Egress file = student /egress-recorder full-screen, not this chrome.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!canManageSession) return;
    if (!recordingActive && !recordingPaused) return;
    setToolsCollapsed(true);
    setMobileToolsOpen(false);
  }, [canManageSession, recordingActive, recordingPaused]);

  useEffect(() => {
    if (qaPaused) {
      if (pauseStartedAtRef.current == null) {
        pauseStartedAtRef.current = Date.now();
        setPauseNowSec(0);
      }
      const id = window.setInterval(() => {
        const started = pauseStartedAtRef.current;
        if (started == null) return;
        setPauseNowSec(Math.floor((Date.now() - started) / 1000));
      }, 1000);
      return () => window.clearInterval(id);
    }
    if (pauseStartedAtRef.current != null) {
      const added = Math.floor(
        (Date.now() - pauseStartedAtRef.current) / 1000,
      );
      setPauseAccumulatedSec((s) => s + Math.max(0, added));
      pauseStartedAtRef.current = null;
      setPauseNowSec(0);
    }
    return undefined;
  }, [qaPaused]);

  const livePauseSec =
    pauseAccumulatedSec + (qaPaused ? Math.max(0, pauseNowSec) : 0);

  const qaRemainingSec =
    qaPaused && qaEndsAt
      ? Math.max(0, Math.floor((qaEndsAt - Date.now()) / 1000))
      : 0;

  const expectedStudentCount = useMemo(() => {
    let count = 0;
    for (const p of liveParticipants) {
      const identity = String(p.identity || "").trim();
      if (!identity || identity === trainerId) continue;
      if (parseParticipantRole(p.metadata, p.identity) === "trainer") continue;
      if (isParticipantWaiting(p.metadata)) continue;
      count += 1;
    }
    return count;
  }, [liveParticipants, trainerId]);

  const syncedClock = useLiveSessionClockSync({
    role: "student",
    enabled: liveKit.connected && !canManageSession,
  });

  const planSegmentCounts = useMemo(() => {
    let completed = 0;
    let total = 0;
    let trainingActive = false;
    for (const t of topics) {
      if (t.subtopics?.length) {
        for (const s of t.subtopics) {
          total += 1;
          if (s.completed || s.skipped) {
            completed += 1;
          } else if (s.actualStartAt) {
            trainingActive = true;
          }
        }
      } else {
        total += 1;
        if (t.completed || t.skipped) {
          completed += 1;
        } else if (t.actualStartAt) {
          trainingActive = true;
        }
      }
    }
    if (!trainingActive && completed > 0) {
      trainingActive = true;
    }
    return { completed, total, trainingActive };
  }, [topics]);
  const planTrainingActive = planSegmentCounts.trainingActive;

  const timingFallbackClock = useMemo(
    () => syncedClockFromTopicPlan(topics),
    [topics],
  );
  const effectiveSyncedClock = mergeSessionClockWithTopicTiming(
    syncedClock,
    timingFallbackClock,
  );

  const primaryTrainerInRoom = useMemo(() => {
    if (canManageSession) return true;
    const assigned = assignedTrainerId.trim();
    if (!assigned) return false;
    const assignedLower = assigned.toLowerCase();
    for (const p of liveParticipants) {
      const identity = String(p.identity || "").trim();
      if (!identity) continue;
      if (identity !== assigned && identity.toLowerCase() !== assignedLower) {
        continue;
      }
      if (parseParticipantRole(p.metadata, p.identity) === "trainer") {
        return true;
      }
    }
    return false;
  }, [canManageSession, assignedTrainerId, liveParticipants]);

  const coTrainerClockRelay =
    isTrainer &&
    !canManageSession &&
    liveKit.connected &&
    assignedTrainerId.length > 0 &&
    !primaryTrainerInRoom;

  const progressRole = canManageSession ? role : "student";
  const progress = useLiveTopicProgress({
    sessionId,
    role: progressRole,
    topics,
    startTime,
    scheduledStartTime: scheduledStartTime || startTime,
    sessionStartedAtIso,
    durationMinutes,
    running: canManageSession
      ? !qaPaused &&
        !awaitingGraspFeedback &&
        (liveKit.connected || liveKit.connecting)
      : (Boolean(effectiveSyncedClock)
          ? !effectiveSyncedClock?.qaPaused
          : planTrainingActive) &&
        (liveKit.connected || liveKit.connecting),
    pauseAccumulatedSec: canManageSession
      ? livePauseSec
      : (effectiveSyncedClock?.pauseAccumulatedSec ?? 0),
    qaRemainingSec,
    syncedClock: canManageSession ? null : effectiveSyncedClock,
  });

  const clockPublish = useMemo(() => {
    if (!canManageSession) return null;
    const topicActualDurationsSec: Record<string, number> = {};
    const completedTopicIds: string[] = [];
    const skippedTopicIds: string[] = [];
    for (const t of progress.topics) {
      if (t.completed || t.skipped) {
        completedTopicIds.push(t.id);
        if (t.skipped) skippedTopicIds.push(t.id);
        const parentSec = durationSecForClockPublish(t);
        if (parentSec != null) topicActualDurationsSec[t.id] = parentSec;
      }
      for (const sub of t.subtopics || []) {
        if (!sub.completed && !sub.skipped) continue;
        completedTopicIds.push(sub.id);
        if (sub.skipped) skippedTopicIds.push(sub.id);
        const subSec = durationSecForClockPublish(sub);
        if (subSec != null) topicActualDurationsSec[sub.id] = subSec;
      }
    }
    return {
      projectedEndTime:
        awaitingGraspFeedback && frozenProjectedEnd
          ? frozenProjectedEnd
          : progress.projectedEndTime,
      qaPaused,
      topicTimeBonusSec: progress.topicTimeBonusSec,
      pauseAccumulatedSec: livePauseSec,
      completedTopicIds,
      skippedTopicIds,
      topicActualDurationsSec,
      topicElapsedSec: progress.topicElapsedSec,
      subtopicElapsedSec: progress.hasSubtopics
        ? progress.subtopicElapsedSec
        : 0,
      sessionElapsedSec: progress.sessionElapsedSec,
      topicStarted: progress.topicStarted,
      currentTopicId: progress.currentTopic?.id ?? null,
      currentSubtopicId:
        progress.hasSubtopics && progress.topicStarted
          ? progress.currentSubtopic?.id ?? null
          : null,
      currentSubtopicActualStartAt:
        progress.hasSubtopics &&
        progress.topicStarted &&
        progress.subtopicStarted
          ? progress.currentSubtopic?.actualStartAt || null
          : null,
    };
  }, [
    canManageSession,
    awaitingGraspFeedback,
    frozenProjectedEnd,
    progress.projectedEndTime,
    progress.topicTimeBonusSec,
    progress.topicElapsedSec,
    progress.subtopicElapsedSec,
    progress.sessionElapsedSec,
    progress.topicStarted,
    progress.subtopicStarted,
    progress.topics,
    progress.currentTopic?.id,
    progress.hasSubtopics,
    progress.currentSubtopic?.id,
    progress.currentSubtopic?.actualStartAt,
    qaPaused,
    livePauseSec,
  ]);

  useLiveSessionClockSync({
    role: "trainer",
    enabled: liveKit.connected && canManageSession,
    publish: clockPublish,
  });

  const relayClockPublish = useMemo(() => {
    if (!coTrainerClockRelay) return null;
    const topicActualDurationsSec: Record<string, number> = {};
    const completedTopicIds: string[] = [];
    const skippedTopicIds: string[] = [];
    for (const t of progress.topics) {
      if (t.completed || t.skipped) {
        completedTopicIds.push(t.id);
        if (t.skipped) skippedTopicIds.push(t.id);
        const parentSec = durationSecForClockPublish(t);
        if (parentSec != null) topicActualDurationsSec[t.id] = parentSec;
      }
      for (const sub of t.subtopics || []) {
        if (!sub.completed && !sub.skipped) continue;
        completedTopicIds.push(sub.id);
        if (sub.skipped) skippedTopicIds.push(sub.id);
        const subSec = durationSecForClockPublish(sub);
        if (subSec != null) topicActualDurationsSec[sub.id] = subSec;
      }
    }
    return {
      projectedEndTime: progress.projectedEndTime,
      qaPaused: Boolean(effectiveSyncedClock?.qaPaused),
      topicTimeBonusSec: progress.topicTimeBonusSec,
      pauseAccumulatedSec: effectiveSyncedClock?.pauseAccumulatedSec ?? 0,
      completedTopicIds,
      skippedTopicIds,
      topicActualDurationsSec,
      topicElapsedSec: progress.topicElapsedSec,
      subtopicElapsedSec: progress.hasSubtopics
        ? progress.subtopicElapsedSec
        : 0,
      sessionElapsedSec: progress.sessionElapsedSec,
      topicStarted: progress.topicStarted,
      currentTopicId: progress.currentTopic?.id ?? null,
      currentSubtopicId:
        progress.hasSubtopics && progress.topicStarted
          ? progress.currentSubtopic?.id ?? null
          : null,
      currentSubtopicActualStartAt:
        progress.hasSubtopics &&
        progress.topicStarted &&
        progress.subtopicStarted
          ? progress.currentSubtopic?.actualStartAt || null
          : null,
    };
  }, [
    coTrainerClockRelay,
    effectiveSyncedClock?.qaPaused,
    effectiveSyncedClock?.pauseAccumulatedSec,
    progress.projectedEndTime,
    progress.topicTimeBonusSec,
    progress.topicElapsedSec,
    progress.subtopicElapsedSec,
    progress.sessionElapsedSec,
    progress.topicStarted,
    progress.subtopicStarted,
    progress.topics,
    progress.currentTopic?.id,
    progress.hasSubtopics,
    progress.currentSubtopic?.id,
    progress.currentSubtopic?.actualStartAt,
  ]);

  useLiveSessionClockSync({
    role: "trainer",
    enabled: coTrainerClockRelay,
    publish: relayClockPublish,
  });

  const policyPublish = useMemo(() => {
    if (!canManageSession) return null;
    return {
      distraction_detection_enabled:
        liveSessionSettings.distraction_detection_enabled,
      student_pip_allowed: liveSessionSettings.student_pip_allowed,
      students_can_unmute: liveSessionSettings.students_can_unmute,
      students_can_enable_camera:
        liveSessionSettings.students_can_enable_camera,
      at: Date.now(),
    };
  }, [
    canManageSession,
    liveSessionSettings.distraction_detection_enabled,
    liveSessionSettings.student_pip_allowed,
    liveSessionSettings.students_can_unmute,
    liveSessionSettings.students_can_enable_camera,
  ]);

  useLiveSessionPolicySync({
    role: isTrainer ? "trainer" : "student",
    enabled: liveKit.connected,
    publish: policyPublish,
  });

  const quizSync = useLiveQuizSync({
    role: isTrainer ? "trainer" : "student",
    // LiveKit data channel: quiz push / scores / grasp while connected
    enabled: liveKit.connected,
  });

  const collabSync = useLiveCollaborationSync({
    enabled: liveKit.connected,
    localIdentity: trainerId || displayName,
    trainerIdentity: trainerId || undefined,
  });

  const [chatThreadsBootstrap, setChatThreadsBootstrap] = useState<
    { thread_id: string; peer: { identity: string; role: string; display_name: string } }[]
  >([]);
  /** true after bootstrap attempt finishes; panels wait so they don't double-hit REST. */
  const [collabBootstrapDone, setCollabBootstrapDone] = useState(false);
  /** true only on successful bootstrap — panels skip list REST. */
  const [collabBootstrapped, setCollabBootstrapped] = useState(false);
  const [collabSince, setCollabSince] = useState("");
  const [collabHasMore, setCollabHasMore] = useState({
    chatPublic: false,
    chatDm: false,
    qa: false,
    announcements: false,
  });

  // One REST bootstrap for chat/Q&A/announcements — panels skip list hydrates.
  useEffect(() => {
    if (!liveKit.connected || !sessionId || !isTrainer) return;
    setCollabBootstrapDone(false);
    setCollabBootstrapped(false);
    toolBadgesSeededRef.current = false;
    setSeenChatMessageIds(new Set());
    setSeenQaKeys(new Set());
    setSeenAnnouncementIds(new Set());
    let cancelled = false;
    void (async () => {
      try {
        const { fetchCollabBootstrap } = await import("./liveCollaborationApi");
        // Trainers need full Q&A / announcement history (not join-window only).
        // Students still use joinedSince for mid-session catch-up.
        const data = await fetchCollabBootstrap({
          sessionId,
          trainerId: trainerId || undefined,
        });
        if (cancelled) return;
        if (!cancelled) setCollabSince("");
        setCollabHasMore({
          chatPublic: data.chatPublicHasMore,
          chatDm: data.chatDmHasMore,
          qa: data.qaHasMore,
          announcements: data.announcementsHasMore,
        });
        collabSync.setChatAccess(data.chatAccess);
        collabSync.setChatMessages(data.chatPublic);
        collabSync.setQaItems(data.qa);
        collabSync.setAnnouncements(data.announcements);
        if (data.activePoll) collabSync.setActivePoll(data.activePoll);
        setChatThreadsBootstrap(data.chatThreads);
        // Baseline: historical items are already "seen" — badge only for later arrivals.
        toolBadgesSeededRef.current = true;
        setSeenChatMessageIds(
          new Set(
            data.chatPublic
              .map((m) => String(m.message_id || "").trim())
              .filter(Boolean),
          ),
        );
        setSeenQaKeys(
          new Set(data.qa.map((q) => `${q.qa_id}:${q.status}`)),
        );
        setSeenAnnouncementIds(
          new Set(
            data.announcements
              .map((a) => String(a.announcement_id || "").trim())
              .filter(Boolean),
          ),
        );
        setCollabBootstrapped(true);
      } catch (err) {
        console.error("collab bootstrap failed", err);
      } finally {
        if (!cancelled) {
          const bootSince = new Date().toISOString();
          collabPollSinceRef.current = bootSince;
          qaPollSinceRef.current = bootSince;
          setCollabBootstrapDone(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveKit.connected, sessionId, isTrainer, trainerId]);

  // REST fallback when LiveKit fan-out misses. One interval for Q&A +
  // announcements + public chat so we never stack overlapping XHRs.
  // Instant path: server/client LiveKit publishData.
  const COLLAB_REST_POLL_INTERVAL_MS = 30_000;

  useEffect(() => {
    if (!liveKit.connected || !sessionId || !isTrainer || !collabBootstrapDone) {
      return;
    }
    const poller = createGuardedAsyncPoller({
      intervalMs: COLLAB_REST_POLL_INTERVAL_MS,
      shouldStop: () => isAuthSessionExpired(),
      run: async () => {
        try {
          const { fetchQaItems, fetchAnnouncements, fetchChatMessages } =
            await import("./liveCollaborationApi");
          const { mergeOlderById } = await import("./collabHistory");
          const qaSince = qaPollSinceRef.current || undefined;
          const collabSinceVal = collabPollSinceRef.current || undefined;
          const [qaPage, annPage, chatPage] = await Promise.all([
            fetchQaItems(sessionId, { limit: 50, since: qaSince }),
            fetchAnnouncements(sessionId, {
              limit: 50,
              since: collabSinceVal,
            }),
            fetchChatMessages({
              sessionId,
              channel: "public",
              since: collabSinceVal,
              limit: 50,
            }),
          ]);
          for (const q of qaPage.items) {
            collabSync.notifyIncomingQa(q);
          }
          if (qaPage.items.length) {
            collabSync.setQaItems((prev) =>
              mergeOlderById(qaPage.items, prev, (q) => q.qa_id),
            );
            qaPollSinceRef.current = new Date().toISOString();
          }
          for (const a of annPage.items) {
            collabSync.notifyIncomingAnnouncement(a);
          }
          for (const m of chatPage.items) {
            collabSync.notifyIncomingChat(m);
          }
          if (annPage.items.length) {
            collabSync.setAnnouncements((prev) =>
              mergeOlderById(annPage.items, prev, (a) => a.announcement_id),
            );
          }
          if (chatPage.items.length) {
            collabSync.setChatMessages((prev) => {
              let next = prev;
              for (const m of chatPage.items) {
                const idx = next.findIndex(
                  (x) => x.message_id === m.message_id,
                );
                if (idx < 0) next = [...next, m];
              }
              return next;
            });
          }
          collabPollSinceRef.current = new Date().toISOString();
        } catch (err) {
          if (isAuthHttpError(err) || isAuthSessionExpired()) {
            poller.stop();
            return;
          }
          console.warn("collab poll failed", err);
        }
      },
    });
    const onAuthExpired = () => poller.stop();
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onAuthExpired);
    poller.start(true);
    return () => {
      poller.stop();
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onAuthExpired);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveKit.connected, sessionId, isTrainer, collabBootstrapDone]);

  // (announcements + chat + Q&A share the collab REST poll above)

  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  const reactionSync = useLiveReactionSync({
    enabled: liveKit.connected,
    fromId: trainerId || displayName,
    fromName: displayName,
  });

  const hostModeration = useHostModerationSync({
    role: isTrainer ? "trainer" : "student",
    enabled: liveKit.connected && isTrainer,
  });

  // ---------------------------------------------------------------
  // step 1: REST hydrate + short poll while quiz report / absorption is open
  // LiveKit still delivers realtime rows; REST fills gaps under load.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!isTrainer || !sessionId) return;
    if (!quizScoresOpen && !absorptionOpen) return;
    let cancelled = false;
    let hydrateInFlight = false;
    const mergeSubmissions = (
      incoming: typeof quizSync.submissions,
    ) => {
      if (!incoming.length) return;
      quizSync.setSubmissions((prev) => {
        const map = new Map(
          prev.map((s) => [`${s.push_id}:${s.student_id}`, s]),
        );
        for (const s of incoming) {
          map.set(`${s.push_id}:${s.student_id}`, s);
        }
        return Array.from(map.values());
      });
    };
    const hydrate = async () => {
      if (cancelled || hydrateInFlight || isAuthSessionExpired()) return;
      hydrateInFlight = true;
      try {
        if (quizScoresOpen) {
          const [scores, grasp] = await Promise.all([
            fetchLiveQuizScores(sessionId),
            fetchGraspVotes(sessionId),
          ]);
          if (cancelled) return;
          if (scores.activeQuiz) quizSync.setActiveQuiz(scores.activeQuiz);
          if (scores.quizzes.length > 0) {
            setSessionQuizzes(scores.quizzes);
          } else if (scores.activeQuiz) {
            setSessionQuizzes([scores.activeQuiz]);
          }
          mergeSubmissions(scores.submissions);
          if (grasp.votes.length > 0) {
            quizSync.setGraspVotes(grasp.votes);
          }
        } else {
          const grasp = await fetchGraspVotes(sessionId);
          if (cancelled) return;
          quizSync.setGraspVotes(grasp.votes);
        }
      } catch (err) {
        if (isAuthHttpError(err) || isAuthSessionExpired()) {
          cancelled = true;
          return;
        }
        console.error("Trainer quiz / grasp hydrate failed", err);
      } finally {
        hydrateInFlight = false;
      }
    };
    void hydrate();
    const id = window.setInterval(() => {
      if (cancelled || isAuthSessionExpired()) {
        window.clearInterval(id);
        return;
      }
      void hydrate();
    }, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrainer, quizScoresOpen, absorptionOpen, sessionId]);

  const persistQaPaused = (
    paused: boolean,
    options?: { endTraining?: boolean },
  ) => {
    void setSessionQaPaused(sessionId, paused, options).catch((err) => {
      console.warn("qa pause persist failed", err);
    });
  };

  useEffect(() => {
    if (!canManageSession) return;
    if (!qaPaused || !qaEndsAt) return;
    const id = window.setInterval(() => {
      if (isQaPauseOver(Date.now(), qaEndsAt)) {
        setQaPaused(false);
        setQaEndsAt(null);
        persistQaPaused(false);
      }
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qaPaused, qaEndsAt, sessionId]);

  // Auto-open Manage Topic when planned topic/subtopic time is reached (once; stay open).
  const timeLimitTriggered = progress.hasSubtopics
    ? progress.subtopicTimeLimitReached
    : progress.topicTimeLimitReached;
  const activeSegmentId =
    progress.hasSubtopics && progress.currentSubtopic
      ? progress.currentSubtopic.id
      : progress.currentTopic?.id ?? "";
  useEffect(() => {
    if (!canManageSession || !timeLimitTriggered) return;
    const topic = progress.currentTopic;
    if (!topic || topic.completed) return;
    if (!progress.topicStarted) return;
    if (activeSegmentId && topicCompletionLockRef.current.has(activeSegmentId)) {
      return;
    }
    if (
      pendingCompleteAfterPostRef.current ||
      awaitingPostQuizEnd ||
      awaitingGraspFeedback ||
      quizPrompt ||
      quizPushBusy
    ) {
      return;
    }
    setManageTopicEarlyAccess(false);
    setManageTopicOpen(true);
  }, [
    isTrainer,
    timeLimitTriggered,
    progress.currentTopic?.id,
    progress.currentTopic?.completed,
    progress.topicStarted,
    activeSegmentId,
    awaitingPostQuizEnd,
    awaitingGraspFeedback,
    quizPrompt,
    quizPushBusy,
  ]);

  // Live absentee count for top bar (invited students not present).
  // Do not restart on participantCount — that stacked XHRs on every join/leave.
  // Pause while the engagement panel owns the same endpoint.
  useEffect(() => {
    if (!isTrainer || !sessionId || activeTool === "engagement") {
      if (activeTool === "engagement") return;
      setAbsenteeCount(null);
      return;
    }
    const poller = createGuardedAsyncPoller({
      intervalMs: 30_000,
      run: async () => {
        try {
          const report = await fetchAttendanceEngagement(sessionId);
          setAbsenteeCount(Math.max(0, report.summary.absentees || 0));
          const peers = (report.students || [])
            .filter((s) => s.present || s.active)
            .map((s) => ({
              identity: String(s.student_id || "").trim(),
              display_name: String(s.name || s.student_id || "").trim(),
            }))
            .filter((s) => s.identity);
          setEngagementStudentPeers(peers);
        } catch {
          /* keep last known count */
        }
      },
    });
    poller.start(true);
    return () => poller.stop();
  }, [isTrainer, sessionId, activeTool]);

  const connectionLabel = liveKit.connectionQualityLabel;
  const connectionTone = (() => {
    switch (liveKit.connectionQualityLabel) {
      case "Excellent":
        return "excellent" as const;
      case "Good":
        return "good" as const;
      case "Poor":
        return "poor" as const;
      case "Lost":
        return "lost" as const;
      case "Offline":
        return "offline" as const;
      default:
        return "unknown" as const;
    }
  })();

  const persistTopicTiming = async (partial: {
    topic_id: string;
    topic_title?: string;
    actual_start_at?: string;
    actual_end_at?: string;
    actual_duration_sec?: number;
    planned_duration_min?: number;
    skipped?: boolean;
    subtopics?: Array<{
      subtopic_id: string;
      subtopic_title?: string;
      actual_start_at?: string;
      actual_end_at?: string;
      actual_duration_sec?: number;
      planned_duration_min?: number;
      skipped?: boolean;
    }>;
  }) => {
    try {
      await upsertTopicTiming(sessionId, partial);
    } catch (err) {
      console.warn("topic timing persist failed", err);
    }
  };

  const handleStartTopic = () => {
    if (!canManageSession) return;
    const topic = progress.currentTopic;
    if (!topic || topic.completed) return;
    if (progress.topicStarted) return;
    // Block next-topic / pre-quiz until grasp window finishes
    if (awaitingPostQuizEnd || awaitingGraspFeedback) return;
    const topicOnlyPre = !progress.hasSubtopics && Boolean(topic.hasPreQuiz);
    const startedAt = topicOnlyPre ? null : progress.startCurrentTopic();
    if (startedAt) {
      // Subtopic clock starts after pre-quiz push/skip (or immediately if no pre).
      let subStartedAt: string | null = null;
      const firstSub = progress.hasSubtopics ? progress.currentSubtopic : null;
      if (firstSub && !firstSub.hasPreQuiz) {
        subStartedAt = progress.startCurrentSubtopic(firstSub.id, {
          forceNow: true,
        });
      }
      // Await timing persist so student presence/join is not skipped as training_not_active.
      void (async () => {
        await persistTopicTiming({
          topic_id: topic.id,
          topic_title: topic.name,
          actual_start_at: startedAt,
          planned_duration_min: topic.durationMin,
          ...(subStartedAt && firstSub
            ? {
                subtopics: [
                  {
                    subtopic_id: firstSub.id,
                    subtopic_title: firstSub.name,
                    actual_start_at: subStartedAt,
                    planned_duration_min: firstSub.durationMin,
                  },
                ],
              }
            : {}),
        });
      })();
    }
    // Pre-quiz: subtopic-level when present, otherwise topic-level
    if (progress.hasSubtopics) {
      if (progress.currentSubtopic?.hasPreQuiz) {
        setQuizPrompt({ slot: "pre" });
      }
    } else if (topic.hasPreQuiz) {
      setQuizPrompt({ slot: "pre" });
    }
  };

  /** Teaching clock begins when pre-quiz is pushed or skipped. */
  const beginSubtopicTeachingAfterPre = () => {
    const topic = progress.currentTopic;
    if (!topic) return;
    const sub =
      progress.hasSubtopics && progress.currentSubtopic
        ? progress.currentSubtopic
        : null;
    if (sub) {
      const startIso = progress.startCurrentSubtopic(sub.id, { forceNow: true });
      if (!startIso) return;
      void persistTopicTiming({
        topic_id: topic.id,
        topic_title: topic.name,
        subtopics: [
          {
            subtopic_id: sub.id,
            subtopic_title: sub.name,
            actual_start_at: startIso,
            planned_duration_min: sub.durationMin,
          },
        ],
      });
      return;
    }
    const startIso = progress.startCurrentTopic();
    if (!startIso) return;
    void persistTopicTiming({
      topic_id: topic.id,
      topic_title: topic.name,
      actual_start_at: startIso,
      planned_duration_min: topic.durationMin,
    });
  };

  /** Last topic finished → planned Q&A timer + board (once per session). */
  const startAutoQaAfterLastTopic = () => {
    if (autoQaStartedRef.current) {
      persistQaPaused(true, { endTraining: true });
      return;
    }
    autoQaStartedRef.current = true;
    const alreadyLive = qaPaused && qaEndsAt != null && qaEndsAt > Date.now();
    if (!alreadyLive) {
      setQaPaused(true);
      setQaEndsAt(qaPauseEndsAtMs(Date.now(), plannedQaMinutes));
    }
    setQaPauseModalOpen(false);
    setManageTopicOpen(false);
    setQuizPrompt(null);
    setDeferredQuizPrompt(null);
    setToolsWidth((w) => Math.max(w, 420));
    setToolsCollapsed(false);
    setActiveTool("qa");
    persistQaPaused(true, { endTraining: true });
  };

  const finishTopicAdvance = (skipped: boolean) => {
    const topic = progress.currentTopic;
    const incompleteBefore = progress.topics.filter(
      (t) => !t.completed && !t.skipped,
    );
    const currentIdx = incompleteBefore.findIndex((t) => t.id === topic?.id);
    const nextTopic =
      currentIdx >= 0 ? incompleteBefore[currentIdx + 1] || null : null;
    // True when this advance finishes the only remaining open topic.
    const completingLast =
      Boolean(topic) &&
      !topic?.completed &&
      !topic?.skipped &&
      incompleteBefore.length === 1 &&
      incompleteBefore[0]?.id === topic?.id;

    const result = progress.completeCurrentTopic({ skipped });
    pendingCompleteAfterPostRef.current = false;
    pendingAdvanceModeRef.current = "topic";
    setAwaitingPostQuizEnd(false);
    setAwaitingGraspFeedback(false);
    setFrozenProjectedEnd(null);
    setGraspEndsAt(null);
    setGraspTopicId(null);
    setGraspRemainingSec(0);
    graspAdvanceDoneRef.current = false;
    graspWaitActiveRef.current = false;
    if (result) {
      const subPayload =
        topic?.subtopics?.map((sub) => ({
          subtopic_id: sub.id,
          subtopic_title: sub.name,
          actual_start_at: sub.actualStartAt,
          actual_end_at: sub.actualEndAt,
          actual_duration_sec: sub.actualDurationSec,
          planned_duration_min: sub.durationMin,
          skipped: Boolean(sub.skipped),
        })) ?? undefined;
      void persistTopicTiming({
        topic_id: result.topicId,
        topic_title: topic?.name,
        actual_start_at: result.actualStartAt,
        actual_end_at: result.actualEndAt,
        actual_duration_sec: result.actualDurationSec,
        planned_duration_min: topic?.durationMin,
        skipped: result.skipped,
        ...(subPayload && subPayload.length
          ? { subtopics: subPayload }
          : {}),
      });
    }

    // Next topics auto-start — Start topic is only for the first topic
    if (nextTopic) {
      pendingAutoStartTopicIdRef.current = nextTopic.id;
    }

    // Last topic done → auto-start planned Q&A and end training attendance.
    if (completingLast) {
      startAutoQaAfterLastTopic();
    }
  };

  /** Complete current subtopic, then start next (with pre-quiz) or finish parent topic. */
  const finishSubtopicAdvance = (skipped: boolean) => {
    const topic = progress.currentTopic;
    const nextSub =
      topic?.subtopics && topic.subtopics.length > 0
        ? topic.subtopics[progress.subtopicIndex + 1] ?? null
        : null;

    pendingCompleteAfterPostRef.current = false;
    setAwaitingPostQuizEnd(false);
    setAwaitingGraspFeedback(false);
    setFrozenProjectedEnd(null);
    setGraspEndsAt(null);
    setGraspTopicId(null);
    setGraspRemainingSec(0);
    graspAdvanceDoneRef.current = false;
    graspWaitActiveRef.current = false;

    const result = progress.completeCurrentSubtopic({ skipped });
    if (!result || !topic) {
      pendingAdvanceModeRef.current = "topic";
      return;
    }

    void persistTopicTiming({
      topic_id: topic.id,
      topic_title: topic.name,
      subtopics: [
        {
          subtopic_id: result.subtopicId,
          subtopic_title: result.subtopicName,
          actual_start_at: result.actualStartAt,
          actual_end_at: result.actualEndAt,
          actual_duration_sec: result.durationSec,
          planned_duration_min: result.plannedDurationMin,
          skipped: result.skipped,
        },
      ],
    });

    if (result.autoCompleteTopic) {
      pendingAdvanceModeRef.current = "topic";
      finishTopicAdvance(skipped);
      return;
    }

    const startIso = nextSub
      ? nextSub.hasPreQuiz
        ? null
        : progress.startCurrentSubtopic(nextSub.id, { forceNow: true })
      : null;
    pendingAdvanceModeRef.current = "topic";
    if (nextSub?.hasPreQuiz) {
      setQuizPrompt({ slot: "pre" });
    }
    if (nextSub && startIso) {
      void persistTopicTiming({
        topic_id: topic.id,
        topic_title: topic.name,
        subtopics: [
          {
            subtopic_id: nextSub.id,
            subtopic_title: nextSub.name,
            actual_start_at: startIso,
            planned_duration_min: nextSub.durationMin,
          },
        ],
      });
    }
  };

  const completePendingAdvance = (skipped = false) => {
    if (pendingAdvanceModeRef.current === "subtopic") {
      finishSubtopicAdvance(skipped);
    } else {
      finishTopicAdvance(skipped);
    }
  };

  /** After post-quiz ends: hold topic advance until grasp votes or timer. */
  const beginGraspFeedbackWait = (topicId: string) => {
    if (!topicId) {
      completePendingAdvance(false);
      return;
    }
    if (graspWaitActiveRef.current || graspAdvanceDoneRef.current) return;
    graspWaitActiveRef.current = true;
    setAwaitingPostQuizEnd(false);
    setAwaitingGraspFeedback(true);
    setFrozenProjectedEnd(progress.projectedEndTime);
    setGraspTopicId(topicId);
    setGraspEndsAt(Date.now() + GRASP_FEEDBACK_SECONDS * 1000);
    setGraspRemainingSec(GRASP_FEEDBACK_SECONDS);
    graspAdvanceDoneRef.current = false;

    // Instant fan-out so students open grasp without waiting for another path.
    const topicTitle =
      (progress.currentSubtopic?.id === topicId
        ? progress.currentSubtopic.name
        : null) ||
      (progress.currentTopic?.id === topicId
        ? progress.currentTopic.name
        : null) ||
      progress.topics.find((t) => t.id === topicId)?.name ||
      progress.currentSubtopic?.name ||
      progress.currentTopic?.name ||
      "Topic";
    void quizSync.publishGraspPrompt({
      topic_id: topicId,
      topic_title: topicTitle,
    });
  };

  const completeAfterGraspFeedback = () => {
    if (graspAdvanceDoneRef.current) return;
    graspAdvanceDoneRef.current = true;
    const topicId = graspTopicId;
    if (topicId && sessionId) {
      const topicTitle =
        progress.topics.find((t) => t.id === topicId)?.name ||
        progress.currentTopic?.name ||
        "Topic";
      void finalizeGraspFeedback({
        sessionId,
        topicId,
        topicTitle,
      }).catch((err) => {
        console.error("finalizeGraspFeedback failed", err);
      });
    }
    completePendingAdvance(false);
  };

  // Safety net: if every topic is done (e.g. after grasp) and Q&A never started
  useEffect(() => {
    if (!canManageSession) return;
    if (autoQaStartedRef.current || qaPaused) return;
    if (awaitingPostQuizEnd || awaitingGraspFeedback) return;
    if (pendingCompleteAfterPostRef.current) return;
    // Only after a completion cycle this visit (avoid rejoin of old sessions)
    if (topicCompletionLockRef.current.size === 0) return;
    if (progress.topics.length === 0) return;
    const allDone = progress.topics.every((t) => t.completed || t.skipped);
    if (!allDone) return;
    startAutoQaAfterLastTopic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTrainer,
    qaPaused,
    awaitingPostQuizEnd,
    awaitingGraspFeedback,
    progress.topics,
  ]);

  // Auto-start next topic after advance (pre-quiz prompt if configured)
  useEffect(() => {
    if (!canManageSession) return;
    if (awaitingGraspFeedback || awaitingPostQuizEnd) return;
    const nextId = pendingAutoStartTopicIdRef.current;
    if (!nextId) return;
    const topic = progress.currentTopic;
    if (!topic || topic.completed || topic.id !== nextId) return;
    if (progress.topicStarted) {
      pendingAutoStartTopicIdRef.current = null;
      return;
    }
    pendingAutoStartTopicIdRef.current = null;
    handleStartTopic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTrainer,
    progress.currentTopic?.id,
    progress.topicStarted,
    progress.topics,
    awaitingGraspFeedback,
    awaitingPostQuizEnd,
  ]);

  // All students submitted → end live quiz UI (and unlock post-quiz advance)
  useEffect(() => {
    if (!canManageSession) return;
    const quiz = quizSync.activeQuiz;
    if (!quiz || quiz.status !== "active") return;
    const forPush = quizSync.submissions.filter(
      (s) => s.push_id === quiz.push_id,
    );
    if (forPush.length === 0) return;
    const unique = new Set(
      forPush.map((s) => String(s.student_id || "").trim()).filter(Boolean),
    );
    const expected = expectedStudentCount;
    const doneByFlag = forPush.some((s) => Boolean(s.quiz_completed));
    const doneByCount = expected > 0 && unique.size >= expected;
    if (!doneByFlag && !doneByCount) return;
    quizSync.setActiveQuiz({ ...quiz, status: "completed" });
    setSessionQuizzes((prev) =>
      prev.map((q) =>
        q.push_id === quiz.push_id ? { ...q, status: "completed" as const } : q,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTrainer,
    quizSync.activeQuiz?.push_id,
    quizSync.activeQuiz?.status,
    quizSync.submissions,
    liveKit.participantCount,
  ]);

  // Post-quiz timer ended → start grasp feedback window (then advance)
  useEffect(() => {
    if (!canManageSession || !awaitingPostQuizEnd) return;
    const quiz = quizSync.activeQuiz;
    if (!quiz || quiz.slot !== "post") return;
    if (isLiveQuiz(quiz)) return;
    if (postQuizAdvancePushIdRef.current === quiz.push_id) return;
    if (!pendingCompleteAfterPostRef.current) return;
    postQuizAdvancePushIdRef.current = quiz.push_id;
    beginGraspFeedbackWait(quiz.topic_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTrainer,
    awaitingPostQuizEnd,
    quizSync.activeQuiz?.push_id,
    quizSync.activeQuiz?.status,
    quizSync.activeQuiz?.pushed_at,
    quizSync.activeQuiz?.duration_seconds,
  ]);

  // Keep checking live post-quiz expiry while awaiting grasp window start
  useEffect(() => {
    if (!canManageSession || !awaitingPostQuizEnd) return;
    const id = window.setInterval(() => {
      const quiz = quizSync.activeQuiz;
      if (!quiz || !pendingCompleteAfterPostRef.current) return;
      if (quiz.slot !== "post") return;
      if (isLiveQuiz(quiz)) return;
      if (postQuizAdvancePushIdRef.current === quiz.push_id) return;
      postQuizAdvancePushIdRef.current = quiz.push_id;
      beginGraspFeedbackWait(quiz.topic_id);
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageSession, awaitingPostQuizEnd, quizSync.activeQuiz?.push_id]);

  // Grasp feedback: countdown + early unlock when all students submitted
  useEffect(() => {
    if (!canManageSession || !awaitingGraspFeedback || !graspEndsAt) return;
    const tick = () => {
      const remaining = Math.max(
        0,
        Math.ceil((graspEndsAt - Date.now()) / 1000),
      );
      setGraspRemainingSec(remaining);

      const topicId = graspTopicId;
      const expected = expectedStudentCount;
      const unique = new Set(
        quizSync.graspVotes
          .filter((v) => !topicId || v.topic_id === topicId)
          .map((v) => String(v.student_id || "").trim())
          .filter(Boolean),
      );
      const allSubmitted = expected === 0 || unique.size >= expected;
      if (remaining <= 0 || allSubmitted) {
        completeAfterGraspFeedback();
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canManageSession,
    awaitingGraspFeedback,
    graspEndsAt,
    graspTopicId,
    quizSync.graspVotes,
    expectedStudentCount,
  ]);

  const beginTopicCompletion = (topicId: string): boolean => {
    // ---------------------------------------------------------------
    // step 1: One completion cycle per topic (blocks double post-quiz)
    // ---------------------------------------------------------------
    if (!topicId) return false;
    if (topicCompletionLockRef.current.has(topicId)) return false;
    topicCompletionLockRef.current.add(topicId);
    setManageTopicOpen(false);
    return true;
  };

  const beginTopicManageDecision = (
    segmentId: string,
    action: TopicManageAction,
  ): boolean => {
    if (!segmentId) return false;
    // more-time can happen again after the extended limit elapses — key includes
    // current limit so a later overtime gets a fresh decision.
    const limitSec = progress.hasSubtopics
      ? progress.subtopicTimeLimitSec
      : progress.topicTimeLimitSec;
    const key =
      action === "more-time"
        ? `more:${segmentId}:${limitSec}`
        : `done:${segmentId}:${action}`;
    if (topicManageDecisionRef.current.has(key)) return false;
    topicManageDecisionRef.current.add(key);
    setManageTopicOpen(false);
    return true;
  };

  const publishTopicManageDecision = (
    action: TopicManageAction,
    segmentId: string,
    topicId: string,
  ) => {
    if (applyingRemoteTopicManageRef.current) return;
    if (!room?.localParticipant || !segmentId) return;
    void room.localParticipant
      .publishData(
        encodeLiveTopicManage({
          action,
          segment_id: segmentId,
          topic_id: topicId,
          at: Date.now(),
          by: room.localParticipant.identity,
        }) as Uint8Array<ArrayBuffer>,
        { reliable: true },
      )
      .catch((err) => {
        console.warn("topic-manage fanout failed", err);
      });
  };

  const handleTopicAction = (action: TopicManageAction) => {
    const topic = progress.currentTopic;
    if (!topic || topic.completed) {
      setManageTopicOpen(false);
      return;
    }

    const segmentId =
      progress.hasSubtopics && progress.currentSubtopic
        ? progress.currentSubtopic.id
        : topic.id;

    if (!beginTopicManageDecision(segmentId, action)) {
      setManageTopicOpen(false);
      return;
    }

    // Fan out to other trainers first so their modals close immediately.
    publishTopicManageDecision(action, segmentId, topic.id);

    if (action === "more-time") {
      if (progress.hasSubtopics && progress.currentSubtopic) {
        progress.addSubtopicMoreTime(10);
      } else {
        progress.addMoreTime(10);
      }
      setManageTopicOpen(false);
      return;
    }

    // Subtopic-level: pre/post quiz + grasp per subtopic (same as topics)
    if (progress.hasSubtopics && progress.currentSubtopic) {
      const sub = progress.currentSubtopic;
      if (!beginTopicCompletion(sub.id)) {
        setManageTopicOpen(false);
        return;
      }
      pendingAdvanceModeRef.current = "subtopic";

      if (action === "skip") {
        finishSubtopicAdvance(true);
        return;
      }

      if (sub.hasPostQuiz) {
        if (postQuizPushedTopicIdsRef.current.has(sub.id)) {
          finishSubtopicAdvance(false);
          return;
        }
        pendingCompleteAfterPostRef.current = true;
        setQuizPrompt({ slot: "post", afterComplete: true });
        return;
      }

      finishSubtopicAdvance(false);
      return;
    }

    if (!beginTopicCompletion(topic.id)) {
      setManageTopicOpen(false);
      return;
    }
    pendingAdvanceModeRef.current = "topic";

    if (action === "skip") {
      finishTopicAdvance(true);
      return;
    }

    // Completed → optional post-quiz once, then advance
    if (topic.hasPostQuiz) {
      if (postQuizPushedTopicIdsRef.current.has(topic.id)) {
        // Post already sent for this topic — just advance
        finishTopicAdvance(false);
        return;
      }
      pendingCompleteAfterPostRef.current = true;
      setQuizPrompt({ slot: "post", afterComplete: true });
      return;
    }
    finishTopicAdvance(false);
  };

  // Peer trainers: Apply on one client → close modal + apply for everyone.
  useEffect(() => {
    if (!isTrainer || !room) return;

    const onData = (
      payload: Uint8Array,
      participant?: { identity?: string } | null,
    ) => {
      const msg = decodeLiveTopicManage(payload);
      if (!msg) return;
      const selfId = String(room.localParticipant?.identity || "").trim();
      const fromId = String(
        participant?.identity || msg.by || "",
      ).trim();
      if (selfId && fromId && selfId === fromId) return;

      // Always dismiss local overtime modal when a peer decides.
      setManageTopicOpen(false);

      const topic = progress.currentTopic;
      const localSegmentId =
        progress.hasSubtopics && progress.currentSubtopic
          ? progress.currentSubtopic.id
          : topic?.id ?? "";
      // Ignore stale packets for a different segment.
      if (
        localSegmentId &&
        msg.segment_id &&
        localSegmentId !== msg.segment_id
      ) {
        return;
      }

      applyingRemoteTopicManageRef.current = true;
      try {
        handleTopicAction(msg.action);
      } finally {
        applyingRemoteTopicManageRef.current = false;
      }
    };

    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
    // handleTopicAction closes over latest progress; re-bind when segment changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTrainer,
    room,
    progress.currentTopic?.id,
    progress.currentSubtopic?.id,
    progress.hasSubtopics,
  ]);

  const handlePushQuiz = async (slot: QuizSlot) => {
    if (!canManageSession || quizPushBusy) return;
    const topic = progress.currentTopic;
    if (!topic || topic.completed) return;
    // Pre-quiz / next topic blocked while students are on grasp feedback
    if (slot === "pre" && (awaitingPostQuizEnd || awaitingGraspFeedback)) {
      return;
    }

    const sub =
      progress.hasSubtopics && progress.currentSubtopic
        ? progress.currentSubtopic
        : null;
    const targetId = sub?.id ?? topic.id;
    const targetName = sub?.name ?? topic.name;

    // Block duplicate post-quiz for the same topic/subtopic
    if (slot === "post") {
      if (postQuizPushedTopicIdsRef.current.has(targetId)) {
        setQuizPrompt(null);
        if (pendingCompleteAfterPostRef.current) {
          beginGraspFeedbackWait(targetId);
        }
        return;
      }
      const alreadyPushed = sessionQuizzes.some(
        (q) =>
          q.topic_id === targetId &&
          q.slot === "post" &&
          (isLiveQuiz(q) || q.status === "completed" || q.status === "active"),
      );
      if (alreadyPushed) {
        postQuizPushedTopicIdsRef.current.add(targetId);
        setQuizPrompt(null);
        if (pendingCompleteAfterPostRef.current) {
          // If prior post already ended, start grasp window; else wait for quiz timer
          const livePost = sessionQuizzes.find(
            (q) =>
              q.topic_id === targetId &&
              q.slot === "post" &&
              isLiveQuiz(q),
          );
          if (!livePost) {
            if (postQuizAdvancePushIdRef.current == null) {
              const ended = sessionQuizzes.find(
                (q) =>
                  q.topic_id === targetId &&
                  q.slot === "post",
              );
              postQuizAdvancePushIdRef.current = ended?.push_id ?? targetId;
            }
            beginGraspFeedbackWait(targetId);
          } else {
            setAwaitingPostQuizEnd(true);
          }
        }
        return;
      }
    }

    setQuizPushBusy(true);
    setQuizPushError(null);
    try {
      const quiz = await pushLiveQuiz({
        sessionId,
        topicId: targetId,
        topicTitle: targetName,
        slot,
      });
      if (slot === "post") {
        postQuizPushedTopicIdsRef.current.add(targetId);
      }
      // Fan out immediately — do not await other UI work first.
      void quizSync.publishQuiz(quiz);
      setSessionQuizzes((prev) => {
        const rest = prev.filter((q) => q.push_id !== quiz.push_id);
        return [quiz, ...rest];
      });
      markQuizScoresSeen();
      openQuizScoresReport();
      setManageTopicOpen(false);
      setQuizPrompt(null);
      if (slot === "post" && pendingCompleteAfterPostRef.current) {
        // Advance when post-quiz timer ends (not on Start topic)
        setAwaitingPostQuizEnd(true);
        postQuizAdvancePushIdRef.current = null;
      } else if (slot === "pre") {
        // Pre released → mark subtopic started; wait full planned time before post.
        beginSubtopicTeachingAfterPre();
      }
    } catch (err) {
      console.error(`${slot}-quiz push failed`, err);
      setQuizPushError(liveQuizPushErrorMessage(err));
      // Allow retry only if push never succeeded
      if (slot === "post") {
        postQuizPushedTopicIdsRef.current.delete(targetId);
        topicCompletionLockRef.current.delete(targetId);
        pendingCompleteAfterPostRef.current = false;
      }
    } finally {
      setQuizPushBusy(false);
    }
  };

  const handleQuizPromptConfirm = () => {
    if (!quizPrompt) return;
    setDeferredQuizPrompt(null);
    void handlePushQuiz(quizPrompt.slot);
  };

  const handleQuizPromptSkip = () => {
    setQuizPushError(null);
    const wasPostComplete = quizPrompt?.afterComplete;
    const skippedSlot = quizPrompt?.slot;
    const topic = progress.currentTopic;
    const sub =
      progress.hasSubtopics && progress.currentSubtopic
        ? progress.currentSubtopic
        : null;
    const targetId = sub?.id ?? topic?.id ?? null;
    const targetName = sub?.name ?? topic?.name ?? "";
    setQuizPrompt(null);
    setDeferredQuizPrompt(null);

    // Skip pre-quiz → start teaching clock for this subtopic (planned time from now).
    if (skippedSlot === "pre") {
      beginSubtopicTeachingAfterPre();
      return;
    }

    // Skip post-quiz (including when the bank has no questions) → open grasp
    // for students, then advance after the grasp window — same as post ending.
    if (
      skippedSlot === "post" &&
      (wasPostComplete || pendingCompleteAfterPostRef.current) &&
      targetId
    ) {
      beginGraspFeedbackWait(targetId);
      return;
    }

    if (wasPostComplete || pendingCompleteAfterPostRef.current) {
      completePendingAdvance(false);
    }
  };

  const quizSubmissionKey = (s: {
    push_id: string;
    student_id: string;
    submitted_at?: number;
  }) => `${s.push_id}:${s.student_id}:${s.submitted_at ?? 0}`;

  const markQuizScoresSeen = (submissions = quizSync.submissions) => {
    // ---------------------------------------------------------------
    // step 1: Clear Quiz Scores badge for everything currently loaded
    // ---------------------------------------------------------------
    setSeenQuizScoreKeys(
      new Set(submissions.map((s) => quizSubmissionKey(s))),
    );
  };

  const unseenQuizScoreCount = useMemo(() => {
    return quizSync.submissions.filter(
      (s) => !seenQuizScoreKeys.has(quizSubmissionKey(s)),
    ).length;
  }, [quizSync.submissions, seenQuizScoreKeys]);

  const graspVoteKey = (v: {
    student_id: string;
    topic_id: string;
    submitted_at?: number;
  }) => `${v.student_id}:${v.topic_id}:${v.submitted_at ?? 0}`;

  const markAbsorptionSeen = (votes = quizSync.graspVotes) => {
    // ---------------------------------------------------------------
    // step 1: Clear Topic Absorption badge for everything currently loaded
    // ---------------------------------------------------------------
    setSeenGraspVoteKeys(new Set(votes.map((v) => graspVoteKey(v))));
  };

  const unseenAbsorptionCount = useMemo(() => {
    return quizSync.graspVotes.filter(
      (v) => !seenGraspVoteKeys.has(graspVoteKey(v)),
    ).length;
  }, [quizSync.graspVotes, seenGraspVoteKeys]);

  const closeQuizScoresReport = () => {
    markQuizScoresSeen();
    setActiveTool(null);
    setMobileToolsOpen(false);
    if (
      pendingCompleteAfterPostRef.current &&
      awaitingPostQuizEnd &&
      quizSync.activeQuiz &&
      !isLiveQuiz(quizSync.activeQuiz)
    ) {
      if (
        postQuizAdvancePushIdRef.current !== quizSync.activeQuiz.push_id
      ) {
        postQuizAdvancePushIdRef.current = quizSync.activeQuiz.push_id;
        beginGraspFeedbackWait(quizSync.activeQuiz.topic_id);
      }
    }
  };

  const openQuizScoresReport = () => {
    markQuizScoresSeen();
    setToolsWidth((w) => Math.max(w, 420));
    if (isMobile) setMobileToolsOpen(true);
    setActiveTool("quiz-scores");
    setToolsCollapsed(false);
  };

  const openAbsorptionPanel = () => {
    // ---------------------------------------------------------------
    // step 1: Dock in Meeting Tools (resizable) + clear badge
    // ---------------------------------------------------------------
    markAbsorptionSeen();
    setToolsWidth((w) => Math.max(w, 420));
    if (isMobile) setMobileToolsOpen(true);
    setActiveTool("absorption");
  };

  // While Quiz Scores is open, keep badge cleared for live arrivals
  useEffect(() => {
    if (!quizScoresOpen) return;
    markQuizScoresSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizScoresOpen, quizSync.submissions]);

  // While Topic Absorption is open, keep badge cleared for live arrivals
  useEffect(() => {
    if (!absorptionOpen) return;
    markAbsorptionSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absorptionOpen, quizSync.graspVotes]);

  const qaBadgeKey = (q: {
    qa_id: string;
    status: string;
  }) => `${q.qa_id}:${q.status}`;

  const markChatSeen = (messages = collabSync.chatMessages) => {
    setSeenChatMessageIds((prev) => {
      const next = new Set(prev);
      for (const m of messages) {
        const id = String(m.message_id || "").trim();
        if (id) next.add(id);
      }
      return next;
    });
  };

  const markQaSeen = (items = collabSync.qaItems) => {
    setSeenQaKeys((prev) => {
      const next = new Set(prev);
      for (const q of items) next.add(qaBadgeKey(q));
      return next;
    });
  };

  const markAnnouncementsSeen = (items = collabSync.announcements) => {
    setSeenAnnouncementIds((prev) => {
      const next = new Set(prev);
      for (const a of items) {
        const id = String(a.announcement_id || "").trim();
        if (id) next.add(id);
      }
      return next;
    });
  };

  const unreadChatCount = useMemo(() => {
    const self = String(trainerId || "").trim();
    return collabSync.chatMessages.filter((m) => {
      const id = String(m.message_id || "").trim();
      if (!id || seenChatMessageIds.has(id)) return false;
      const sender = String(m.sender?.identity || "").trim();
      return !self || sender !== self;
    }).length;
  }, [collabSync.chatMessages, seenChatMessageIds, trainerId]);

  const unreadQaCount = useMemo(() => {
    const self = String(trainerId || "").trim();
    return collabSync.qaItems.filter((q) => {
      if (seenQaKeys.has(qaBadgeKey(q))) return false;
      // Trainer: new open questions from students
      if (q.status !== "open") return false;
      const asker = String(q.asked_by?.identity || "").trim();
      return !self || asker !== self;
    }).length;
  }, [collabSync.qaItems, seenQaKeys, trainerId]);

  const unreadAnnouncementCount = useMemo(() => {
    const self = String(trainerId || "").trim();
    return collabSync.announcements.filter((a) => {
      const id = String(a.announcement_id || "").trim();
      if (!id || seenAnnouncementIds.has(id)) return false;
      const authorId = String(a.author?.identity || "").trim();
      return !self || authorId !== self;
    }).length;
  }, [collabSync.announcements, seenAnnouncementIds, trainerId]);

  // Bootstrap failed → panel REST hydrate: treat first loaded lists as already seen.
  useEffect(() => {
    if (!collabBootstrapDone || collabBootstrapped || toolBadgesSeededRef.current)
      return;
    if (
      collabSync.qaItems.length === 0 &&
      collabSync.chatMessages.length === 0 &&
      collabSync.announcements.length === 0
    ) {
      return;
    }
    toolBadgesSeededRef.current = true;
    markChatSeen();
    markQaSeen();
    markAnnouncementsSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    collabBootstrapDone,
    collabBootstrapped,
    collabSync.qaItems,
    collabSync.chatMessages,
    collabSync.announcements,
  ]);

  useEffect(() => {
    if (activeTool !== "chat") return;
    markChatSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool, collabSync.chatMessages]);

  useEffect(() => {
    if (activeTool !== "qa") return;
    markQaSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool, collabSync.qaItems]);

  useEffect(() => {
    if (activeTool !== "announcements") return;
    markAnnouncementsSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool, collabSync.announcements]);

  const openMeetingTool = (id: MeetingToolId) => {
    // ---------------------------------------------------------------
    // step 1: Route known tools to existing room actions
    // ---------------------------------------------------------------
    if (id === "quiz" || id === "quiz-scores") {
      openQuizScoresReport();
      return;
    }
    if (id === "absorption") {
      openAbsorptionPanel();
      return;
    }
    if (id === "engagement") {
      setEngagementFilter("all");
      setToolsWidth((w) => Math.max(w, 420));
      if (isMobile) setMobileToolsOpen(true);
      setActiveTool("engagement");
      setToolsCollapsed(false);
      return;
    }
    if (id === "distraction") {
      setToolsWidth((w) => Math.max(w, 480));
      if (isMobile) setMobileToolsOpen(true);
      setActiveTool("distraction");
      setToolsCollapsed(false);
      return;
    }
    if (id === "manage-topic" || id === "session-topics" || id === "recording") {
      if (!canManageSession) return;
    }
    if (id === "manage-topic") {
      if (!progress.topicStarted) {
        handleStartTopic();
      } else {
        const topic = progress.currentTopic;
        const segId =
          progress.hasSubtopics && progress.currentSubtopic
            ? progress.currentSubtopic.id
            : topic?.id ?? "";
        if (
          topic &&
          !topic.completed &&
          segId &&
          !topicCompletionLockRef.current.has(segId) &&
          !pendingCompleteAfterPostRef.current &&
          !awaitingPostQuizEnd &&
          !awaitingGraspFeedback
        ) {
          setManageTopicEarlyAccess(true);
          setManageTopicOpen(true);
        }
      }
      setActiveTool(null);
      setMobileToolsOpen(false);
      return;
    }
    if (id === "settings") {
      if (isMobile) setMobileToolsOpen(true);
      setActiveTool("settings");
      return;
    }
    // Q&A board opens in Meeting Tools; pause modal stays on control bar
    // ---------------------------------------------------------------
    // step 2: Other tools → inline detail panel
    // ---------------------------------------------------------------
    if (id === "session-topics") {
      setToolsWidth((w) => Math.max(w, 420));
    }
    if (id === "chat") markChatSeen();
    if (id === "qa") markQaSeen();
    if (isMobile) setMobileToolsOpen(true);
    setActiveTool(id);
  };

  const openEngagementWithFilter = (filter: AttendanceEngagementFilter) => {
    setEngagementFilter(filter);
    setToolsWidth((w) => Math.max(w, 420));
    if (isMobile) setMobileToolsOpen(true);
    setActiveTool("engagement");
    setToolsCollapsed(false);
  };

  const toolsBadgeTotal =
    (unreadChatCount || 0) +
    (unreadQaCount || 0) +
    (unreadAnnouncementCount || 0) +
    (waitingAdmitCount || 0) +
    (handRaises.raisedHands.length || 0) +
    (unseenAbsorptionCount || 0) +
    (unseenQuizScoreCount || 0);

  const badgeByTool = {
    absorption: unseenAbsorptionCount || undefined,
    "quiz-scores": unseenQuizScoreCount || undefined,
    chat: unreadChatCount || undefined,
    qa: unreadQaCount || undefined,
    announcements: unreadAnnouncementCount || undefined,
    participants:
      (waitingAdmitCount || 0) + (handRaises.raisedHands.length || 0) ||
      undefined,
  };

  const renderActiveToolBody = () => {
    if (!activeTool) return null;
    if (activeTool === "participants" && isTrainer) {
      return (
        <ParticipantModerationPanel
          sessionId={sessionId}
          settings={liveSessionSettings}
          handRaises={handRaises}
          onSettingsChange={(saved) => {
            setLiveSessionSettings(saved);
            if (sessionMeta) {
              (sessionMeta as LiveSessionNavState).sessionSettings = saved;
            }
          }}
          onAfterAction={({ action, identity, trackSource }) => {
            void hostModeration.publishHostAction({
              action,
              identity,
              track_source: trackSource || "microphone",
            });
          }}
        />
      );
    }
    if (activeTool === "settings" && isTrainer) {
      return (
        <MediaJoinSettingsPanel
          sessionId={sessionId}
          initialSettings={liveSessionSettings}
          onSaved={(saved) => {
            setLiveSessionSettings(saved);
            if (sessionMeta) {
              (sessionMeta as LiveSessionNavState).sessionSettings = saved;
            }
          }}
        />
      );
    }
    if (activeTool === "recording" && canManageSession) {
      return (
        <SessionRecordingPanel
          sessionId={sessionId}
          onStatusChange={setRecordingStatus}
          onRecordingStarted={handleRecordingStarted}
        />
      );
    }
    if (activeTool === "absorption" && isTrainer) {
      return (
        <TopicAbsorptionPanel
          open
          sessionId={sessionId}
          topics={progress.topics}
          currentTopicName={
            progress.currentTopic && !progress.currentTopic.completed
              ? progress.currentTopic.name
              : undefined
          }
          graspVotes={quizSync.graspVotes}
          doubtsCount={
            quizSync.graspVotes.filter(
              (v) =>
                v.level === "partial" || v.level === "not_understood",
            ).length
          }
          feedbackCount={quizSync.graspVotes.length}
        />
      );
    }
    if (activeTool === "engagement" && isTrainer) {
      return (
        <AttendanceEngagementPanel
          sessionId={sessionId}
          initialFilter={engagementFilter}
        />
      );
    }
    if (activeTool === "distraction" && isTrainer) {
      return <DistractionReportPanel sessionId={sessionId} />;
    }
    if (activeTool === "session-topics" && canManageSession) {
      return (
        <SessionTopicsPage
          topics={progress.topics}
          currentTopic={progress.currentTopic}
          topicProgressPercent={progress.topicProgressPercent}
          topicElapsedLabel={progress.topicElapsedLabel}
          topicTimeLimitSec={progress.topicTimeLimitSec}
          currentSubtopicName={progress.currentSubtopic?.name ?? null}
          subtopicElapsedLabel={progress.subtopicElapsedLabel}
          subtopicProgressPercent={progress.subtopicProgressPercent}
          subtopicTimeLimitSec={progress.subtopicTimeLimitSec}
          onMarkCompleted={() => {
            handleTopicAction("completed");
          }}
        />
      );
    }
    if (activeTool === "qa" && isTrainer) {
      return (
        <TrainerQaPanel
          sessionId={sessionId}
          trainerId={trainerId}
          trainerName={displayName}
          items={collabSync.qaItems}
          hydrateReady={collabBootstrapDone}
          skipHydrate={collabBootstrapped}
          joinedSince={collabSince}
          initialHasMore={collabHasMore.qa}
          focusQaId={focusQaId}
          onFocusHandled={() => setFocusQaId(null)}
          onItems={collabSync.setQaItems}
          onPublish={collabSync.publishQa}
        />
      );
    }
    if (activeTool === "announcements" && isTrainer) {
      return (
        <TrainerAnnouncementsPanel
          sessionId={sessionId}
          trainerId={trainerId}
          trainerName={displayName}
          items={collabSync.announcements}
          hydrateReady={collabBootstrapDone}
          skipHydrate={collabBootstrapped}
          joinedSince={collabSince}
          initialHasMore={collabHasMore.announcements}
          onItems={collabSync.setAnnouncements}
          onPublish={collabSync.publishAnnouncement}
          onRemoveItem={collabSync.removeAnnouncement}
        />
      );
    }
    if (activeTool === "polls" && isTrainer) {
      return (
        <TrainerPollsPanel
          sessionId={sessionId}
          trainerId={trainerId}
          trainerName={displayName}
          poll={collabSync.activePoll}
          hydrateReady={collabBootstrapDone}
          skipHydrate={collabBootstrapped}
          onPoll={collabSync.setActivePoll}
          onPublish={collabSync.publishPoll}
        />
      );
    }
    if (activeTool === "quiz-scores" && isTrainer) {
      return (
        <TrainerLiveQuizReport
          active
          quiz={quizSync.activeQuiz}
          quizzes={sessionQuizzes}
          submissions={quizSync.submissions}
          expectedCount={expectedStudentCount}
        />
      );
    }
    if (activeTool === "chat" && isTrainer) {
      return (
        <TrainerChatPanel
          sessionId={sessionId}
          trainerId={trainerId}
          trainerName={displayName}
          messages={collabSync.chatMessages}
          access={collabSync.chatAccess}
          initialThreads={chatThreadsBootstrap}
          hydrateReady={collabBootstrapDone}
          joinedSince={collabSince}
          initialPublicHasMore={collabHasMore.chatPublic}
          initialDmHasMore={collabHasMore.chatDm}
          focusPeerId={focusChatPeerId}
          onFocusPeerHandled={() => setFocusChatPeerId(null)}
          studentPeers={chatStudentPeers}
          onMessages={collabSync.setChatMessages}
          onAccess={collabSync.setChatAccess}
          onPublishMessage={collabSync.publishChat}
          onRemoveMessage={collabSync.removeChatMessage}
          onPublishEngage={collabSync.publishChatEngage}
          onPublishAccess={collabSync.publishChatAccess}
        />
      );
    }
    return null;
  };

  return (
    <div
      style={{
        height: "100dvh",
        maxHeight: "100dvh",
        width: "100%",
        background: "#0f1117",
        color: "#e2e8f0",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {!hideTrainerChrome ? (
      <LiveKitTopBar
        homePath={homePath}
        sessionTitle={sessionTitle}
        role={role}
        participantCount={liveKit.participantCount}
        absenteeCount={isTrainer ? absenteeCount ?? undefined : undefined}
        topics={progress.topics}
        currentTopic={progress.currentTopic}
        topicProgressPercent={progress.topicProgressPercent}
        topicElapsedLabel={progress.topicElapsedLabel}
        sessionTimer={progress.sessionTimerLabel}
        scheduledEndTime={progress.scheduledEndTime}
        expectedEndTime={progress.projectedEndTime}
        connectionLabel={connectionLabel}
        connectionTone={connectionTone}
        recording={canManageSession && recordingActive}
        recordingPaused={canManageSession && recordingPaused}
        displayName={displayName}
        mediaError={liveKit.mediaError}
        doubtsCount={
          quizSync.graspVotes.filter(
            (v) => v.level === "partial" || v.level === "not_understood",
          ).length
        }
        feedbackCount={quizSync.graspVotes.length}
        onOpenAbsorption={isTrainer ? openAbsorptionPanel : undefined}
        onOpenAttendees={
          isTrainer ? () => openEngagementWithFilter("attendees") : undefined
        }
        onOpenAbsentees={
          isTrainer ? () => openEngagementWithFilter("absentees") : undefined
        }
        compact={isMobile}
        topicStarted={progress.topicStarted}
        currentSubtopicId={
          progress.hasSubtopics ? progress.currentSubtopic?.id ?? null : null
        }
        currentSubtopicName={
          progress.hasSubtopics ? progress.currentSubtopic?.name ?? null : null
        }
        subtopicProgressPercent={
          progress.hasSubtopics ? progress.subtopicProgressPercent : undefined
        }
        subtopicElapsedLabel={
          progress.hasSubtopics ? progress.subtopicElapsedLabel : undefined
        }
      />
      ) : null}

      {qaPaused && !hideTrainerChrome ? (
        <div
          style={{
            flexShrink: 0,
            padding: "8px 16px",
            background: "rgba(217,119,6,0.15)",
            borderBottom: "1px solid rgba(245,158,11,0.35)",
            color: "#fcd34d",
            fontSize: 12,
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          Q&A pause active — session and topic timers are frozen
          {qaEndsAt
            ? ` · ends ${new Date(qaEndsAt).toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
              })}`
            : ""}
        </div>
      ) : null}

      {isTrainer &&
      !hideTrainerChrome &&
      handRaises.raisedHands.length > 0 ? (
        <button
          type="button"
          onClick={() => {
            setActiveTool("participants");
            setToolsCollapsed(false);
            if (isMobile) setMobileToolsOpen(true);
          }}
          style={{
            flexShrink: 0,
            width: "100%",
            border: "none",
            padding: "8px 16px",
            background: "rgba(59,130,246,0.18)",
            borderBottom: "1px solid rgba(96,165,250,0.4)",
            color: "#bfdbfe",
            fontSize: 12,
            fontWeight: 700,
            textAlign: "center",
            cursor: "pointer",
          }}
        >
          {handRaises.raisedHands.length === 1
            ? "1 student raised their hand — open Participants"
            : `${handRaises.raisedHands.length} students raised their hands — open Participants`}
        </button>
      ) : null}

      {isTrainer && awaitingGraspFeedback && !hideTrainerChrome ? (
        <div
          style={{
            flexShrink: 0,
            padding: "8px 16px",
            background: "rgba(37,99,235,0.14)",
            borderBottom: "1px solid rgba(96,165,250,0.4)",
            color: "#bfdbfe",
            fontSize: 12,
            fontWeight: 600,
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <span>Post-quiz · Grasp feedback</span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "2px 10px",
              borderRadius: 999,
              background: "rgba(15,23,42,0.55)",
              border: "1px solid rgba(96,165,250,0.45)",
              color: "#fff",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {`${Math.floor(graspRemainingSec / 60)}:${String(
              graspRemainingSec % 60,
            ).padStart(2, "0")}`}
          </span>
          <span style={{ color: "#93c5fd", fontWeight: 500 }}>
            {(() => {
              const expected = Math.max(
                0,
                expectedStudentCount,
              );
              const submitted = new Set(
                quizSync.graspVotes
                  .filter((v) => !graspTopicId || v.topic_id === graspTopicId)
                  .map((v) => String(v.student_id || "").trim())
                  .filter(Boolean),
              ).size;
              return `${submitted}/${expected} submitted · next topic locked`;
            })()}
          </span>
        </div>
      ) : null}

      {/* Main stage + tiles + Meeting Tools (no WaitingAdmitBanner). */}
      <div
        style={{
          flex: 1,
          display: "flex",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            flex: 1,
            display:
              isTrainer && railIdentities.length > 0 ? "grid" : "flex",
            gridTemplateColumns:
              isTrainer && railIdentities.length > 0
                ? "minmax(0, 1fr) 200px"
                : undefined,
            flexDirection:
              isTrainer && railIdentities.length > 0 ? undefined : "column",
            gap: isMobile ? 8 : 12,
            padding: isMobile ? 8 : 12,
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
        <div
          style={{
            position: "relative",
            flex: isTrainer && railIdentities.length > 0 ? undefined : 1,
            borderRadius: 16,
            background: "#1a1d26",
            border: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
          }}
        >
          {liveKit.connecting ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Spinner animation="border" size="sm" className="me-2" />
              Connecting...
            </div>
          ) : liveKit.error ? (
            <div
              style={{
                color: isSessionEndedLiveKitError(liveKit.error)
                  ? "#fcd34d"
                  : "#fca5a5",
                textAlign: "center",
                padding: 24,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
              }}
            >
              <p style={{ margin: 0 }}>{liveKit.error}</p>
              {isSessionEndedLiveKitError(liveKit.error) ? (
                <button
                  type="button"
                  onClick={() => navigate(homePath, { replace: true })}
                  style={{
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 8,
                    padding: "8px 14px",
                    background: "rgba(255,255,255,0.06)",
                    color: "#e2e8f0",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Back to sessions
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  style={{
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 8,
                    padding: "8px 14px",
                    background: "rgba(255,255,255,0.06)",
                    color: "#e2e8f0",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Try again
                </button>
              )}
            </div>
          ) : liveKit.inRoom ? (
            <>
              <LiveKitMainStage
                isTrainer={role === "trainer"}
                connectingLabel="Connecting…"
                emptyTitle={
                  role === "trainer"
                    ? liveKit.isScreenShareEnabled
                      ? "You are presenting"
                      : "Waiting for students"
                    : "You are in the live class"
                }
                emptySubtitle={
                  role === "trainer"
                    ? liveKit.isScreenShareEnabled
                      ? "Stop sharing to see all student cameras here. Your camera stays in Participants."
                      : "Student video feeds appear here in a grid when they join (camera on or off)."
                    : "You’ll see the trainer’s camera and your own here."
                }
                onStageIdentitiesChange={onStageIdentitiesChange}
              />
              <FloatingReactionsOverlay reactions={reactionSync.floating} />
              {documentPip.pipOpen ? (
                <LiveSessionPipBanner
                  reason={
                    liveKit.isScreenShareEnabled ? "share" : "manual"
                  }
                  onBringBack={() => {
                    documentPip.closePip();
                    if (liveKit.isScreenShareEnabled) {
                      setPreferMainWhileSharing(true);
                    }
                  }}
                />
              ) : null}
            </>
          ) : (
            <div
              style={{
                color: "#94a3b8",
                textAlign: "center",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Ready to connect…
            </div>
          )}
        </div>

        {isTrainer && !hideTrainerChrome ? (
          <div
            style={{
              display: railIdentities.length > 0 ? "flex" : "none",
              flexDirection: "column",
              gap: 8,
              minHeight: 0,
              minWidth: 0,
              height: "100%",
              overflow: "hidden",
            }}
          >
            <LiveKitVideoTiles
              isTrainer
              selfName={displayName}
              excludeIdentities={stageIdentities}
              onVisibleIdentitiesChange={onRailIdentitiesChange}
            />
          </div>
        ) : null}
        </div>

        {!isMobile && !hideTrainerChrome && activeTool ? (
          <MeetingToolPanel
            toolId={activeTool}
            onBack={() => {
              if (activeTool === "quiz-scores") {
                closeQuizScoresReport();
              } else {
                setActiveTool(null);
              }
            }}
            width={toolsWidth}
            onResizePointerDown={onToolsResizePointerDown}
          >
            {renderActiveToolBody()}
          </MeetingToolPanel>
        ) : !isMobile && !hideTrainerChrome ? (
          <MeetingToolsRail
            role={isTrainer ? "trainer" : "student"}
            canManageTopics={canManageSession}
            collapsed={toolsCollapsed}
            activeTool={activeTool}
            badgeByTool={badgeByTool}
            onSelectTool={openMeetingTool}
            onToggleCollapsed={() => setToolsCollapsed((v) => !v)}
            width={toolsWidth}
            onResizePointerDown={onToolsResizePointerDown}
          />
        ) : null}
      </div>

      {!hideTrainerChrome &&
      isMobile &&
      (mobileToolsOpen || activeTool) ? (
        <>
          <button
            type="button"
            aria-label="Dismiss meeting tools"
            onClick={() => {
              setActiveTool(null);
              setMobileToolsOpen(false);
            }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1230,
              border: "none",
              background: "rgba(2, 6, 23, 0.55)",
              cursor: "pointer",
              padding: 0,
            }}
          />
          {activeTool ? (
            <MeetingToolPanel
              toolId={activeTool}
              onBack={() => {
                if (activeTool === "quiz-scores") {
                  closeQuizScoresReport();
                } else {
                  setActiveTool(null);
                }
              }}
              width={toolsWidth}
              onResizePointerDown={onToolsResizePointerDown}
              mobile
            >
              {renderActiveToolBody()}
            </MeetingToolPanel>
          ) : (
            <MeetingToolsRail
              role={isTrainer ? "trainer" : "student"}
              canManageTopics={canManageSession}
              collapsed={false}
              activeTool={activeTool}
              badgeByTool={badgeByTool}
              onSelectTool={openMeetingTool}
              onToggleCollapsed={() => {
                setActiveTool(null);
                setMobileToolsOpen(false);
              }}
              width={toolsWidth}
              onResizePointerDown={onToolsResizePointerDown}
              mobile
            />
          )}
        </>
      ) : null}

      {!hideTrainerChrome &&
      isMobile &&
      !mobileToolsOpen &&
      !activeTool ? (
        <button
          type="button"
          onClick={() => setMobileToolsOpen(true)}
          aria-label="Open Meeting Tools"
          style={{
            position: "fixed",
            right: 12,
            bottom: "calc(88px + env(safe-area-inset-bottom, 0px))",
            zIndex: 1220,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            border: "1px solid rgba(255,255,255,0.14)",
            borderRadius: 999,
            background: "#1e3a8a",
            color: "#fff",
            padding: "12px 16px",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 10px 28px rgba(0,0,0,0.45)",
          }}
        >
          <LayoutGrid size={16} />
          Tools
          {toolsBadgeTotal > 0 ? (
            <span
              style={{
                borderRadius: 999,
                background: "#f59e0b",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                padding: "2px 7px",
                minWidth: 18,
                textAlign: "center",
              }}
            >
              {toolsBadgeTotal > 99 ? "99+" : toolsBadgeTotal}
            </span>
          ) : null}
        </button>
      ) : null}

      {documentPip.pipWindow ? (
        <LiveSessionPipPortal
          pipWindow={documentPip.pipWindow}
          isTrainer={isTrainer}
          selfName={displayName}
          isMicrophoneEnabled={liveKit.isMicrophoneEnabled}
          isCameraEnabled={liveKit.isCameraEnabled}
          isScreenShareEnabled={liveKit.isScreenShareEnabled}
          canScreenShare={isTrainer}
          onToggleMicrophone={() => void liveKit.toggleMicrophone()}
          onToggleCamera={() => void liveKit.toggleCamera()}
          onToggleScreenShare={() => {
            void (async () => {
              const enabling = !liveKit.isScreenShareEnabled;
              const next = await liveKit.toggleScreenShare({ withAudio: true });
              if (enabling && next) {
                setPreferMainWhileSharing(false);
              } else if (!enabling) {
                documentPip.closePip();
                setPreferMainWhileSharing(false);
              }
            })();
          }}
          onChat={() => {
            documentPip.closePip();
            if (liveKit.isScreenShareEnabled) {
              setPreferMainWhileSharing(true);
            }
            openMeetingTool("chat");
          }}
          onReaction={(emoji) => {
            void reactionSync.sendReaction(emoji);
          }}
          onExitPip={() => {
            documentPip.closePip();
            if (liveKit.isScreenShareEnabled) {
              setPreferMainWhileSharing(true);
            }
          }}
        />
      ) : null}

      {hideTrainerChrome && !documentPip.pipOpen ? (
        <ScreenSharePrivacyControls
          isMicrophoneEnabled={liveKit.isMicrophoneEnabled}
          onToggleMicrophone={() => void liveKit.toggleMicrophone()}
          onStopShare={() => void liveKit.toggleScreenShare({ withAudio: true })}
          onShowTools={() => setPreferMainWhileSharing(true)}
          entireScreen
        />
      ) : hideTrainerChrome && documentPip.pipOpen ? null : (
      <div style={{ position: "relative", flexShrink: 0, zIndex: 1300 }}>
        {isTrainer && liveKit.isScreenShareEnabled ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              padding: "4px 16px 0",
              flexWrap: "wrap",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 12,
                color: "#fbbf24",
                fontWeight: 600,
              }}
            >
              Tools are visible in Entire Screen share — hide them so students
              don’t see Meeting Tools / top bar.
            </p>
            <button
              type="button"
              onClick={() => {
                setPreferMainWhileSharing(false);
                setActiveTool(null);
                setToolsCollapsed(true);
              }}
              style={{
                border: "1px solid rgba(251,191,36,0.45)",
                borderRadius: 8,
                padding: "4px 10px",
                background: "rgba(251,191,36,0.15)",
                color: "#fde68a",
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Hide tools
            </button>
          </div>
        ) : null}
        {isTrainer ? (
          <SessionDetailsPopover
            open={sessionDetailsOpen}
            details={{
              sessionName: sessionTitle,
              topicCovered:
                (progress.currentTopic && !progress.currentTopic.completed
                  ? progress.currentTopic.name
                  : "") ||
                sessionMeta.topicCovered ||
                progress.topics.map((t) => t.name).join(", ") ||
                sessionTitle,
              courseLabel: sessionMeta.courseLabel,
              batchLabel: sessionMeta.batchLabel,
              category: sessionMeta.category,
              trainerName: resolveTrainerDisplayName(
                sessionMeta.trainerName,
                displayName,
              ),
              trainerAvatarUrl: sessionMeta.trainerAvatarUrl,
              streamingMode: sessionMeta.streamingMode || "one-way",
              sessionTimer: progress.sessionTimerLabel,
            }}
            onClose={() => setSessionDetailsOpen(false)}
          />
        ) : null}
        <LiveKitControlBar
          inRoom={liveKit.inRoom}
          isMicrophoneEnabled={liveKit.isMicrophoneEnabled}
          isCameraEnabled={liveKit.isCameraEnabled}
          isScreenShareEnabled={liveKit.isScreenShareEnabled}
          onToggleMicrophone={() => void liveKit.toggleMicrophone()}
          onToggleCamera={() => void liveKit.toggleCamera()}
          onToggleScreenShare={() => {
            void (async () => {
              const enabling = !liveKit.isScreenShareEnabled;
              const next = await liveKit.toggleScreenShare({ withAudio: true });
              if (enabling && next) {
                // Hide top bar + Meeting Tools so Entire Screen share
                // does not expose trainer chrome to students.
                setPreferMainWhileSharing(false);
                setToolsCollapsed(true);
                setActiveTool(null);
              } else if (!enabling) {
                documentPip.closePip();
                setPreferMainWhileSharing(false);
              }
            })();
          }}
          pipSupported={documentPip.supported}
          pipOpen={documentPip.pipOpen}
          onTogglePip={() => {
            void (async () => {
              if (documentPip.pipOpen) {
                documentPip.closePip();
                // Stay in privacy mode (chrome hidden) while still sharing.
                if (liveKit.isScreenShareEnabled) {
                  setPreferMainWhileSharing(false);
                }
                return;
              }
              setPreferMainWhileSharing(false);
              await documentPip.openPip();
            })();
          }}
          onLeave={() => {
            void endOrLeave();
          }}
          leaveLabel={canManageSession ? "End" : "Leave"}
          leaveDisabled={endingSession || pausingSession}
          onPauseSession={
            canManageSession && isLcmSession
              ? () => {
                  setPauseSessionError(null);
                  // Default: tomorrow same local time
                  const d = new Date();
                  d.setDate(d.getDate() + 1);
                  d.setMinutes(d.getMinutes() - (d.getTimezoneOffset() % 1));
                  const pad = (n: number) => String(n).padStart(2, "0");
                  setPauseResumeLocal(
                    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
                  );
                  setPauseDialogOpen(true);
                }
              : undefined
          }
          pauseSessionDisabled={pausingSession || endingSession}
          compact={isMobile}
          showTrainerTools={isTrainer}
          canPauseForQa={canManageSession}
          canManageTopics={canManageSession}
          sessionDetailsOpen={sessionDetailsOpen}
          onToggleSessionDetails={() =>
            setSessionDetailsOpen((v) => !v)
          }
          qaPaused={qaPaused}
          onPauseForQa={() => {
            if (!canManageSession) return;
            setQaPauseModalOpen(true);
          }}
          onResumeFromQa={() => {
            if (!canManageSession) return;
            setQaPaused(false);
            setQaEndsAt(null);
            persistQaPaused(false);
          }}
          onManageTopic={() => {
            if (!progress.topicStarted) return;
            const topic = progress.currentTopic;
            if (!topic || topic.completed) return;
            const segId =
              progress.hasSubtopics && progress.currentSubtopic
                ? progress.currentSubtopic.id
                : topic.id;
            if (topicCompletionLockRef.current.has(segId)) return;
            if (
              pendingCompleteAfterPostRef.current ||
              awaitingPostQuizEnd ||
              awaitingGraspFeedback
            ) {
              return;
            }
            setManageTopicEarlyAccess(true);
            setManageTopicOpen(true);
          }}
          topicStarted={progress.topicStarted}
          /** Start topic only for the very first topic; later topics auto-start. */
          canStartTopic={
            canManageSession &&
            !progress.topicStarted &&
            progress.topics.length > 0 &&
            progress.topics.every((t) => !t.completed) &&
            !awaitingPostQuizEnd &&
            !awaitingGraspFeedback
          }
          onStartTopic={handleStartTopic}
          trainerToolsDisabled={manageTopicOpen}
          allTopicsComplete={
            progress.topics.length > 0 &&
            progress.topics.every((t) => t.completed || t.skipped)
          }
          onChat={() => openMeetingTool("chat")}
          onReactions={() => setReactionPickerOpen((v) => !v)}
          reactionsPicker={
            <ReactionPicker
              open={reactionPickerOpen}
              onClose={() => setReactionPickerOpen(false)}
              onSelect={(emoji) => {
                void reactionSync.sendReaction(emoji);
              }}
            />
          }
        />
      </div>
      )}

      {endSessionError ? (
        <OfflineDisconnectModal
          open
          title="Could not end session"
          reason={endSessionError}
          onContinueHere={() => setEndSessionError(null)}
          onClose={() => setEndSessionError(null)}
        />
      ) : null}

      {pauseDialogOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Pause session"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10050,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(15,23,42,0.55)",
            padding: 16,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 420,
              borderRadius: 14,
              background: "#0f172a",
              border: "1px solid rgba(255,255,255,0.12)",
              padding: 20,
              color: "#e2e8f0",
            }}
          >
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
              Pause session
            </p>
            <p
              style={{
                margin: "8px 0 0",
                fontSize: 13,
                color: "#94a3b8",
                lineHeight: 1.45,
              }}
            >
              Students will leave the room. Attendance, engagement, and topic
              progress are kept. Enter when you expect to resume.
            </p>
            <label
              style={{
                display: "block",
                marginTop: 16,
                fontSize: 12,
                fontWeight: 600,
                color: "#cbd5e1",
              }}
            >
              Expected resume
              <input
                type="datetime-local"
                value={pauseResumeLocal}
                onChange={(e) => setPauseResumeLocal(e.target.value)}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 6,
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.16)",
                  background: "#1e293b",
                  color: "#f8fafc",
                  fontSize: 14,
                }}
              />
            </label>
            {pauseSessionError ? (
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: 12,
                  color: "#fda4af",
                }}
              >
                {pauseSessionError}
              </p>
            ) : null}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 18,
              }}
            >
              <button
                type="button"
                onClick={() => {
                  if (pausingSession) return;
                  setPauseDialogOpen(false);
                  setPauseSessionError(null);
                }}
                disabled={pausingSession}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "transparent",
                  color: "#e2e8f0",
                  fontWeight: 600,
                  cursor: pausingSession ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmPauseSession()}
                disabled={pausingSession}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "none",
                  background: pausingSession ? "#a16207" : "#d97706",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: pausingSession ? "not-allowed" : "pointer",
                }}
              >
                {pausingSession ? "Pausing…" : "Pause"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isTrainer && liveKit.offlineNotice ? (
        <OfflineDisconnectModal
          open
          title={liveKit.offlineNotice.title}
          reason={liveKit.offlineNotice.reason}
          reconnecting={liveKit.offlineNotice.reconnecting}
          onContinueHere={() => window.location.reload()}
          onClose={leave}
        />
      ) : null}

      {canManageSession ? (
        <>
          <StartQaPauseModal
            open={qaPauseModalOpen && !hideTrainerChrome}
            defaultDurationMinutes={plannedQaMinutes}
            onClose={() => setQaPauseModalOpen(false)}
            onStart={(minutes) => {
              setQaPaused(true);
              setQaEndsAt(qaPauseEndsAtMs(Date.now(), minutes));
              persistQaPaused(true);
              setSessionDetailsOpen(false);
              setManageTopicOpen(false);
              setActiveTool(null);
            }}
          />
          <ManageTopicModal
            open={
              manageTopicOpen &&
              !hideTrainerChrome &&
              Boolean(progress.currentTopic) &&
              !progress.currentTopic?.completed &&
              !topicCompletionLockRef.current.has(activeSegmentId)
            }
            topic={progress.currentTopic}
            elapsedLabel={
              progress.hasSubtopics
                ? progress.subtopicElapsedLabel
                : progress.topicElapsedLabel
            }
            timeLimitLabel={formatTimeLimitLabel(
              progress.hasSubtopics
                ? progress.subtopicTimeLimitSec
                : progress.topicTimeLimitSec,
            )}
            earlyAccess={manageTopicEarlyAccess}
            subtopicName={progress.currentSubtopic?.name ?? null}
            subtopicElapsedLabel={
              progress.hasSubtopics ? progress.subtopicElapsedLabel : undefined
            }
            subtopicTimeLimitLabel={
              progress.hasSubtopics
                ? formatTimeLimitLabel(progress.subtopicTimeLimitSec)
                : undefined
            }
            segmentId={activeSegmentId}
            onClose={() => setManageTopicOpen(false)}
            onApply={handleTopicAction}
          />
          {deferredQuizPrompt &&
          !quizPrompt &&
          !hideTrainerChrome &&
          !(
            deferredQuizPrompt.slot === "pre" &&
            (awaitingGraspFeedback || awaitingPostQuizEnd)
          ) ? (
            <div
              style={{
                position: "fixed",
                right: 20,
                bottom: 96,
                zIndex: 220,
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                borderRadius: 12,
                background: "rgba(15,23,42,0.94)",
                border: "1px solid rgba(96,165,250,0.45)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
              }}
            >
              <span style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 600 }}>
                {deferredQuizPrompt.slot === "pre" ? "Pre" : "Post"}-quiz ready
              </span>
              <button
                type="button"
                onClick={() => {
                  setQuizPrompt(deferredQuizPrompt);
                  setDeferredQuizPrompt(null);
                }}
                style={{
                  border: "none",
                  borderRadius: 8,
                  padding: "6px 10px",
                  background: "#2563eb",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Open again
              </button>
              <button
                type="button"
                onClick={() => setDeferredQuizPrompt(null)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#94a3b8",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Dismiss
              </button>
            </div>
          ) : null}
          <ConfirmPushQuizModal
            open={
              Boolean(quizPrompt) &&
              !hideTrainerChrome &&
              !(
                quizPrompt?.slot === "pre" &&
                (awaitingGraspFeedback || awaitingPostQuizEnd)
              )
            }
            slot={quizPrompt?.slot || "pre"}
            sessionId={sessionId}
            topicId={
              progress.hasSubtopics && progress.currentSubtopic
                ? progress.currentSubtopic.id
                : progress.currentTopic?.id
            }
            topicName={
              progress.hasSubtopics && progress.currentSubtopic
                ? progress.currentSubtopic.name
                : progress.currentTopic?.name || "topic"
            }
            busy={quizPushBusy}
            pushError={quizPushError}
            onConfirm={() => {
              setQuizPushError(null);
              handleQuizPromptConfirm();
            }}
            onSkip={handleQuizPromptSkip}
            onClose={
              quizPrompt?.afterComplete
                ? undefined
                : () => {
                    setQuizPushError(null);
                    setDeferredQuizPrompt(quizPrompt);
                    setQuizPrompt(null);
                  }
            }
          />
        </>
      ) : null}
      {isTrainer && room ? (
        <TrainerWaitingAdmitWatcher
          onWaitingStudent={onWaitingStudent}
          onWaitingCountChange={onWaitingCountChange}
        />
      ) : null}
      {waitingAdmitNotice ? (
        <LiveSessionNoticeToast
          notice={waitingAdmitNotice}
          onDismiss={() => setWaitingAdmitNotice(null)}
          onAction={(notice) => {
            if (notice.action?.type === "open_participants") {
              openMeetingTool("participants");
            }
          }}
          durationMs={3000}
          bottom={
            (handRaiseNotice ? 76 : 0) +
            (collabSync.notices.length > 0
              ? 20 + collabSync.notices.length * 76
              : 20)
          }
        />
      ) : null}
      {handRaiseNotice ? (
        <LiveSessionNoticeToast
          notice={handRaiseNotice}
          onDismiss={() => setHandRaiseNotice(null)}
          onAction={(notice) => {
            if (notice.action?.type === "open_participants") {
              openMeetingTool("participants");
            }
          }}
          durationMs={3000}
          bottom={
            collabSync.notices.length > 0
              ? 20 + collabSync.notices.length * 76
              : 20
          }
        />
      ) : null}
      <LiveSessionNoticeToast
        notices={collabSync.notices}
        onDismiss={(id) => collabSync.dismissNotice(id)}
        onAction={(notice) => {
          const action = notice.action;
          if (!action) return;
          if (action.type === "open_qa") {
            if (action.qaId) setFocusQaId(action.qaId);
            openMeetingTool("qa");
            return;
          }
          if (action.type === "open_chat") {
            if (action.peerId) setFocusChatPeerId(action.peerId);
            openMeetingTool("chat");
            return;
          }
          if (action.type === "open_announcements") {
            openMeetingTool("announcements");
            return;
          }
          if (action.type === "open_polls") {
            openMeetingTool("polls");
            return;
          }
          if (action.type === "open_participants") {
            openMeetingTool("participants");
          }
        }}
        durationMs={3000}
        bottom={20}
      />
    </div>
  );
}

export default function LiveSessionRoom({ role = "trainer" }: LiveSessionRoomProps) {
  useLiveSessionFlowClass();
  const { sessionId = "" } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionMeta =
    (location.state as LiveSessionNavState | null) ?? {};
  const displayName =
    sessionStorage.getItem("Name") ||
    sessionStorage.getItem("email") ||
    (role === "trainer" ? "Trainer" : "Student");

  const [fetchedPlan, setFetchedPlan] = useState<unknown>(sessionMeta.topicPlan);
  const [workshopDayIndex, setWorkshopDayIndex] = useState<number | null>(() =>
    typeof sessionMeta.workshopDayIndex === "number"
      ? sessionMeta.workshopDayIndex
      : null,
  );
  const [fetchedTiming, setFetchedTiming] = useState<unknown[]>([]);
  const [fetchedStart, setFetchedStart] = useState(sessionMeta.startTime || "");
  const [fetchedActualStartIso, setFetchedActualStartIso] = useState<string | null>(
    null,
  );
  const [fetchedScheduledStart, setFetchedScheduledStart] = useState(
    sessionMeta.startTime || "",
  );
  const [fetchedDuration, setFetchedDuration] = useState(
    sessionMeta.durationMinutes ?? 60,
  );
  const [fetchedQaMinutes, setFetchedQaMinutes] = useState<number | null>(null);
  const [fetchedTitle, setFetchedTitle] = useState(
    sessionMeta.sessionName || "",
  );
  const [fetchedExtras, setFetchedExtras] = useState<
    Partial<LiveSessionNavState>
  >(() => ({
    courseLabel:
      sessionMeta.courseLabel ||
      sessionStorage.getItem("meet_course_name") ||
      "",
    batchLabel:
      sessionMeta.batchLabel ||
      sessionStorage.getItem("meet_batch_name") ||
      "",
    category: sessionMeta.category || "",
    trainerName: resolveTrainerDisplayName(
      sessionMeta.trainerName,
      sessionStorage.getItem("Name"),
    ),
    topicCovered: sessionMeta.topicCovered || "",
    sessionSettings: sessionMeta.sessionSettings,
  }));
  const [sessionSettings, setSessionSettings] = useState(
    normalizeSessionSettings(sessionMeta.sessionSettings),
  );
  const [settingsReady, setSettingsReady] = useState(
    Boolean(sessionMeta.sessionSettings) || role !== "trainer",
  );

  useEffect(() => {
    if (!sessionId || role !== "trainer") {
      setSettingsReady(true);
      return;
    }
    setFetchedTiming([]);
    let cancelled = false;
    void (async () => {
      try {
        // In-room session detail / topic_timing — lkapi (not business api).
        const res = await authService.get(`/session/sessions/${sessionId}/`);
        const session = res?.data?.session ?? res?.data ?? null;
        if (!session || cancelled) return;
        if (session.topic_plan || session.topicPlan) {
          setFetchedPlan(session.topic_plan ?? session.topicPlan);
        }
        // Prefer explicit nav day; else the day currently live on the workshop.
        const days = Array.isArray(session.day_schedules)
          ? session.day_schedules
          : [];
        const liveDay = days.find(
          (d: { status?: string; can_join?: boolean; day_index?: number }) =>
            d?.can_join || String(d?.status || "").toLowerCase() === "live",
        );
        const resolvedDay =
          typeof sessionMeta.workshopDayIndex === "number"
            ? sessionMeta.workshopDayIndex
            : typeof liveDay?.day_index === "number"
              ? Number(liveDay.day_index)
              : null;
        if (resolvedDay != null) {
          setWorkshopDayIndex(resolvedDay);
        }
        if (Array.isArray(session.topic_timing)) {
          setFetchedTiming(session.topic_timing);
        }
        if (session.session_settings || session.sessionSettings) {
          const normalized = normalizeSessionSettings(
            session.session_settings ?? session.sessionSettings,
          );
          setSessionSettings(normalized);
          setFetchedExtras((prev) => ({
            ...prev,
            sessionSettings: normalized,
          }));
        }
        if (session.session_title || session.Session_Topic) {
          setFetchedTitle(
            String(session.session_title || session.Session_Topic),
          );
        }
        if (session.live_session_category) {
          setFetchedExtras((prev) => ({
            ...prev,
            category: prev.category || String(session.live_session_category),
          }));
        }
        if (session.session_assigned_to) {
          setFetchedExtras((prev) => ({
            ...prev,
            assignedTrainerId: String(session.session_assigned_to).trim(),
          }));
        }
        // Prefer human-readable trainer name — never show user_id.
        const conductedByName = String(
          session.conductedby ||
            session.conducted_by_name ||
            session.trainer_name ||
            "",
        ).trim();
        if (conductedByName && !looksLikeUserId(conductedByName)) {
          setFetchedExtras((prev) => ({
            ...prev,
            trainerName: conductedByName,
          }));
        }
        if (Array.isArray(session.topic_plan) && session.topic_plan.length) {
          const dayPlan = filterTopicPlanForWorkshopDay(
            session.topic_plan,
            resolvedDay,
          );
          const names = (Array.isArray(dayPlan) ? dayPlan : session.topic_plan)
            .map((t: Record<string, unknown>) =>
              String(t?.title ?? t?.name ?? "").trim(),
            )
            .filter(Boolean);
          if (names.length) {
            setFetchedExtras((prev) => ({
              ...prev,
              topicCovered: names.join(", "),
            }));
          }
        }

        const courseIds = asIdList(session.course_id ?? sessionMeta.courseId);
        const batchIds = asIdList(session.batch_id ?? sessionMeta.batchId);
        const trackIds = asIdList(session.track_id);

        // Prefer server-resolved multi labels when present.
        const apiCourseName = String(session.course_name || "").trim();
        const apiBatchName = String(session.batch_name || "").trim();
        if (apiCourseName || apiBatchName) {
          setFetchedExtras((prev) => ({
            ...prev,
            courseLabel: apiCourseName || prev.courseLabel,
            batchLabel: apiBatchName || prev.batchLabel,
          }));
        }

        // Always resolve all ids when multiple batches/courses exist,
        // or when labels are still missing.
        const needsResolve =
          batchIds.length > 1 ||
          courseIds.length > 1 ||
          (!apiCourseName && !sessionMeta.courseLabel) ||
          (!apiBatchName && (!sessionMeta.batchLabel || batchIds.length > 1));

        if ((courseIds.length || batchIds.length) && needsResolve) {
          const labels = await resolveCourseBatchLabels({
            courseIds,
            batchIds,
            trackId: trackIds[0],
          });
          if (!cancelled) {
            setFetchedExtras((prev) => {
              const preferResolvedBatch =
                batchIds.length > 1 ||
                (labels.batchLabel &&
                  labels.batchLabel.split(",").length >
                    String(prev.batchLabel || "").split(",").filter(Boolean)
                      .length);
              const preferResolvedCourse =
                courseIds.length > 1 ||
                (labels.courseLabel &&
                  labels.courseLabel.split(",").length >
                    String(prev.courseLabel || "").split(",").filter(Boolean)
                      .length);
              return {
                ...prev,
                courseLabel: preferResolvedCourse
                  ? labels.courseLabel || prev.courseLabel
                  : prev.courseLabel || labels.courseLabel,
                batchLabel: preferResolvedBatch
                  ? labels.batchLabel || prev.batchLabel
                  : prev.batchLabel || labels.batchLabel,
              };
            });
          }
        }
        const toClock = (raw: unknown): string => {
          if (!raw) return "";
          if (typeof raw === "string" && raw.includes("T")) {
            const ist = formatIstClock(raw);
            if (ist) return ist;
          }
          return formatClockLabel(String(raw)) || String(raw);
        };

        // Scheduled End stays on original schedule; Expected End uses actual start.
        const scheduledRaw =
          session.session_starttime ||
          session.Start_Time ||
          sessionMeta.startTime;
        const actualRaw =
          session.session_actual_starttime ||
          session.Actual_Start_Time_time ||
          scheduledRaw;
        const scheduledClock = toClock(scheduledRaw);
        const actualClock = toClock(actualRaw);
        if (scheduledClock) setFetchedScheduledStart(scheduledClock);
        if (actualClock) setFetchedStart(actualClock);
        const actualIsoCandidate =
          session.session_actual_starttime || session.session_starttime || "";
        if (actualIsoCandidate) {
          const parsed = Date.parse(String(actualIsoCandidate));
          if (Number.isFinite(parsed)) {
            setFetchedActualStartIso(new Date(parsed).toISOString());
          }
        }

        const qaFromSession = Number(
          session.qa_duration_minutes ?? session.qaDurationMinutes,
        );
        if (Number.isFinite(qaFromSession) && qaFromSession > 0) {
          const minutes = clampQaDurationMinutes(qaFromSession);
          setQaDurationForSession(sessionId, minutes);
          setFetchedQaMinutes(minutes);
        }

        const endRaw =
          session.session_expected_endtime || session.session_endtime;
        const durationBase =
          session.session_actual_starttime || session.session_starttime;
        if (durationBase && endRaw) {
          const start = new Date(durationBase);
          const end = new Date(endRaw);
          if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
            setFetchedDuration(
              Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000)),
            );
          }
        }
      } catch {
        // Nav state / fallback topics still work.
      } finally {
        if (!cancelled) setSettingsReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, role, sessionMeta.startTime]);

  const fetchTopicTimingRows = useCallback(async (): Promise<TopicTimingRecord[]> => {
    // In-room topic_timing poll — lkapi (not business api).
    const res = await authService.get(`/session/sessions/${sessionId}/`);
    const session = res?.data?.session ?? res?.data ?? null;
    const list = session?.topic_timing;
    return Array.isArray(list) ? (list as TopicTimingRecord[]) : [];
  }, [sessionId]);

  useSessionTopicTimingPoll({
    sessionId,
    enabled: settingsReady && role === "trainer" && Boolean(sessionId),
    fetchRows: fetchTopicTimingRows,
    onTiming: (rows) =>
      setFetchedTiming((prev) =>
        mergeTopicTimingIfChanged(
          Array.isArray(prev) ? (prev as TopicTimingRecord[]) : [],
          rows,
        ),
      ),
  });

  const topics = useMemo(() => {
    const start = fetchedStart || sessionMeta.startTime || "";
    const dayFiltered = filterTopicPlanForWorkshopDay(
      fetchedPlan ?? sessionMeta.topicPlan,
      workshopDayIndex ??
        (typeof sessionMeta.workshopDayIndex === "number"
          ? sessionMeta.workshopDayIndex
          : null),
    );
    const base = normalizeTopicPlan(
      dayFiltered,
      fetchedTitle || sessionMeta.sessionName,
      fetchedDuration || sessionMeta.durationMinutes || 60,
      start || undefined,
    );
    return applyTopicTimingRecords(
      base,
      Array.isArray(fetchedTiming)
        ? (fetchedTiming as TopicTimingRecord[])
        : [],
    );
  }, [
    fetchedPlan,
    fetchedTiming,
    sessionMeta.topicPlan,
    sessionMeta.workshopDayIndex,
    workshopDayIndex,
    fetchedTitle,
    sessionMeta.sessionName,
    fetchedDuration,
    sessionMeta.durationMinutes,
    fetchedStart,
    sessionMeta.startTime,
  ]);

  const resolvedMeta: LiveSessionNavState = {
    ...sessionMeta,
    ...fetchedExtras,
    sessionName: fetchedTitle || sessionMeta.sessionName,
    topicCovered:
      fetchedExtras.topicCovered ||
      sessionMeta.topicCovered ||
      "",
    category: fetchedExtras.category || sessionMeta.category,
    trainerName: resolveTrainerDisplayName(
      fetchedExtras.trainerName,
      sessionMeta.trainerName,
      displayName,
    ),
    // Prefer resolved multi-batch/course labels over single filter cache.
    courseLabel:
      fetchedExtras.courseLabel ||
      sessionMeta.courseLabel ||
      "",
    batchLabel:
      fetchedExtras.batchLabel ||
      sessionMeta.batchLabel ||
      "",
    sessionSettings,
  };

  const joinDefaults = joinDefaultsForRole(sessionSettings, role);

  if (!sessionId) {
    return (
      <div className="d-flex min-vh-100 align-items-center justify-content-center bg-dark text-light">
        Missing session id.
      </div>
    );
  }

  if (!settingsReady) {
    return (
      <div className="d-flex min-vh-100 align-items-center justify-content-center bg-dark text-light">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading session settings…
      </div>
    );
  }

  return (
    <LiveKitSessionProvider
      sessionId={sessionId}
      role={role}
      displayName={displayName}
      startWithAudio={joinDefaults.microphone}
      startWithVideo={joinDefaults.camera}
      sessionTitle={resolvedMeta.sessionName}
      skipDeviceCheck
      onDeviceCheckCancel={() =>
        navigate(getViewSessionsPath(getRoutePrefix()), { replace: true })
      }
    >
      <RoomBody
        sessionId={sessionId}
        role={role}
        sessionMeta={resolvedMeta}
        topics={topics}
        startTime={fetchedStart || sessionMeta.startTime || "10:00 AM"}
        scheduledStartTime={
          fetchedScheduledStart || sessionMeta.startTime || "10:00 AM"
        }
        sessionStartedAtIso={fetchedActualStartIso}
        durationMinutes={
          fetchedDuration || sessionMeta.durationMinutes || 60
        }
        plannedQaMinutesFromSession={fetchedQaMinutes}
      />
    </LiveKitSessionProvider>
  );
}
